import { AuthUI, type CharacterPickerResult } from '../AuthUI';
import { getBackgroundForScreen, getBackgroundForCharacter } from '../AuthBackgroundConfig';
import { setLoadingPhaseMusic } from '../../audio/loadingMusic';
import type { CharacterSummary, CreateCharacterResult } from '../../api/AuthApi';
import {
  CHARACTER_CLASSES,
  CHARACTER_RACES,
  CHARACTER_SEXES,
  type CharacterRaceId,
  type CharacterSexId,
  type CharacterRaceDef,
  type CharacterSexDef,
  type CharacterClassDef,
  getAvailableHeadIds,
  getRaceInnateModifiers,
  getClassInnateModifiers,
  getHeadAssetName,
  validateCharacterName,
} from '@shared/types/character';
import { describeModifiers, formatModifierValue, type ModifierDisplayRow } from '@shared/data/modifierDisplay';
import { ColyseusClient } from '../../network/ColyseusClient';
import { ServerMessageType, type CharacterStatusUpdateMessage } from '@shared/types/messages';
import { createTextInput, createButton } from '../formControls';
import { skinScrollable } from '../customScrollbar';
import { getAssetUrl, GAME_FONT_FAMILY, SITE_URL, WIKI_URL } from '@shared/constants';
import { CharacterPreview, type CharacterAppearance } from '../CharacterPreview';
import { loadingLogoWidthCss, LOGO_GLOW_FILTER } from '../loadingLayout';

/**
 * Pantallas de selección y creación de personaje. Extraído de AuthUI (god-object). Toma `host: AuthUI`
 * por constructor y accede a su estado (api, contentLayer, statusClient, sessionToken, chrome) vía
 * `this.host.*`. Ver docs/AUTH_AND_LOGIN.md y la memoria del picker skineado.
 */
export class CharacterPicker {
  constructor(private host: AuthUI) {}

  public async showCreateCharacterForm(sessionToken: string): Promise<CharacterSummary> {
    // Música de la pantalla de creación (crossfade desde la intro). Al volver al picker, éste
    // restaura la intro. Ver docs/SOUND_AND_MUSIC.md.
    setLoadingPhaseMusic('music_createchar');
    return new Promise<CharacterSummary>((resolve) => {
      // El form se construye DENTRO de `build`, que corre mientras la pantalla está en
      // negro (whileHidden de changeBackgroundForScreen): así el fade-out revela el form
      // ya montado en vez de aparecer a medio cargar después del efecto.
      const build = (): void => {
      this.host.contentLayer.innerHTML = '';

      // Label con la URL del sitio en la parte superior centrado
      const aureumLabel = document.createElement('div');
      aureumLabel.textContent = SITE_URL;
      aureumLabel.style.position = 'fixed';
      aureumLabel.style.top = '0';
      aureumLabel.style.left = '50%';
      aureumLabel.style.transform = 'translateX(-50%)';
      aureumLabel.style.padding = '12px';
      aureumLabel.style.color = '#eee';
      aureumLabel.style.fontSize = '14px';
      aureumLabel.style.fontFamily = 'system-ui, sans-serif';
      aureumLabel.style.zIndex = '1000';
      this.host.contentLayer.appendChild(aureumLabel);

      // Layout a PANTALLA COMPLETA anclado a los bordes (mismo patrón que la
      // pantalla de selección, renderCharacterPickerScreen): un root fixed inset:0
      // sobre el fondo del mapa, con cada zona posicionada en absoluto contra los
      // bordes. NO usa createWindowFrame (marco centrado de tamaño fijo) porque ese
      // se recorta en monitores chicos/bajos. El fondo (character_create) es el backdrop.
      const root = document.createElement('div');
      root.style.position = 'fixed';
      root.style.inset = '0';
      root.style.zIndex = '50';
      root.style.fontFamily = 'system-ui, sans-serif';
      root.style.color = '#eee';

      const GOLD = '#d9b25a';

      // --- Logo abajo-CENTRO, a la altura de la barra de acciones (Volver/Crear) ---
      const logo = document.createElement('img');
      logo.alt = 'Aureum';
      logo.style.position = 'absolute';
      logo.style.bottom = '20px';
      logo.style.left = '50%';
      logo.style.transform = 'translateX(-50%)';
      logo.style.height = '48px';
      logo.style.width = 'auto';
      logo.style.maxWidth = '28vw';
      logo.style.pointerEvents = 'none';
      logo.style.zIndex = '7';
      logo.style.filter = LOGO_GLOW_FILTER;
      void this.host.chrome.getProcessedLogoUrl().then((u) => {
        logo.src = u;
      });

      // =====================================================================
      // PREVIEW DEL PERSONAJE (componente vivo con respiración; ver CharacterPreview)
      // =====================================================================
      // Escala entera del muñeco. Se calcula en recomputeScale() con el MISMO
      // criterio que la pantalla de selección (renderCharacterPickerScreen) para
      // que el muñeco se vea idéntico en ambas. El nombre in-game escala con
      // PREVIEW_SCALE (mismo ratio fuente:cuerpo que el juego).
      let PREVIEW_SCALE = 6;

      // =====================================================================
      // CACHE DE IMÁGENES (spritesheets) con redibujo al cargar
      // =====================================================================
      const imgCache = new Map<string, HTMLImageElement>();
      const getReady = (src: string, redraw: () => void): HTMLImageElement | null => {
        let img = imgCache.get(src);
        if (!img) {
          img = new Image();
          imgCache.set(src, img);
          img.src = src;
        }
        if (img.complete && img.naturalWidth > 0) return img;
        img.addEventListener('load', redraw, { once: true });
        return null;
      };

      // Dibuja la cara (frame DOWN, col 0 de la tira 1x4) centrada en un canvas.
      const drawHeadCanvas = (
        canvas: HTMLCanvasElement,
        scale: number,
        headId: number | undefined,
        redraw: () => void
      ): void => {
        const c = canvas.getContext('2d');
        if (!c) return;
        c.setTransform(1, 0, 0, 1, 0, 0);
        c.clearRect(0, 0, canvas.width, canvas.height);
        c.imageSmoothingEnabled = false;
        if (headId === undefined) return;
        const img = getReady(`/assets/entities/head/${getHeadAssetName(headId)}.png`, redraw);
        if (!img) return;
        const fw = img.width / 4;
        const fh = img.height;
        const dw = fw * scale;
        const dh = fh * scale;
        const dx = Math.round((canvas.width - dw) / 2);
        const dy = Math.round((canvas.height - dh) / 2);
        c.drawImage(img, 0, 0, fw, fh, dx, dy, dw, dh);
      };

      // =====================================================================
      // ASSETS DE LA UI DE CREACIÓN (ui/createchar) + helper de tile skineado
      // =====================================================================
      // URL CDN-aware de un asset de la carpeta ui/createchar.
      const ccUrl = (file: string): string => getAssetUrl(`ui/createchar/${file}.png`);

      // Tile cuadrado skineado reusable (razas y sexos): fondo biselado del arte +
      // overlay de marco "seleccionado" que se enciende/apaga. El contenido (canvas
      // de cabeza o icono de sexo) va centrado en `content`. Devuelve el nodo, el
      // contenedor de contenido y un setter de selección.
      interface SkinnedTile {
        el: HTMLDivElement;
        content: HTMLDivElement;
        setSelected: (on: boolean) => void;
      }
      const makeSkinnedTile = (size: number, bgFile: string, selFile: string): SkinnedTile => {
        const el = document.createElement('div');
        el.style.position = 'relative';
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.flex = '0 0 auto';
        el.style.cursor = 'pointer';
        el.style.boxSizing = 'border-box';
        el.style.background = `url('${ccUrl(bgFile)}') center/100% 100% no-repeat`;
        el.style.imageRendering = 'pixelated';

        const content = document.createElement('div');
        content.style.position = 'absolute';
        content.style.inset = '0';
        content.style.display = 'flex';
        content.style.alignItems = 'center';
        content.style.justifyContent = 'center';
        content.style.overflow = 'hidden';
        content.style.imageRendering = 'pixelated';
        el.appendChild(content);

        // Marco de selección: encima de todo, sin robar clicks. Calza exactamente
        // sobre el borde del tile (mismo tamaño) para que el bisel crema no quede
        // corrido respecto al arte de fondo.
        const sel = document.createElement('img');
        sel.src = ccUrl(selFile);
        sel.style.position = 'absolute';
        sel.style.inset = '0';
        sel.style.width = '100%';
        sel.style.height = '100%';
        sel.style.pointerEvents = 'none';
        sel.style.imageRendering = 'pixelated';
        sel.style.display = 'none';
        el.appendChild(sel);

        return {
          el,
          content,
          setSelected: (on: boolean): void => {
            sel.style.display = on ? 'block' : 'none';
          },
        };
      };

      // =====================================================================
      // ESTADO
      // =====================================================================
      let selectedRace = CHARACTER_RACES[0].id as CharacterRaceId;
      let selectedSex = CHARACTER_SEXES[0].id as CharacterSexId;
      let selectedClassId: string = CHARACTER_CLASSES[0].id;
      let availableHeadIds: number[] = [];
      let currentHeadIndex = 0;

      // =====================================================================
      // PRESET GRÁFICO POR CLASE
      // =====================================================================
      // Lo que se le pone al muñeco al elegir una clase. CharacterPreview ya sabe
      // dibujar body/head/weapon/shield/helm/boot; acá decimos QUÉ equipa cada clase.
      // Hoy solo seteamos `bodyId` (body1/2/3 = lo único con sprite todavía); a
      // medida que existan los sprites se agregan weaponId/shieldId/helmId/bootId
      // por clase en `CLASS_GRAPHIC_PRESETS` (override) — NO referenciar IDs sin
      // asset (no se debe mostrar lo que no hicimos).
      const CLASS_BODY_PLACEHOLDER = [1, 2, 3];
      const CLASS_GRAPHIC_PRESETS: Record<string, CharacterAppearance> = {
        // ej. futuro: warrior: { bodyId: 1, weaponId: 5, shieldId: 2, helmId: 3 },
      };
      const classPreset = (classId: string): CharacterAppearance => {
        const override = CLASS_GRAPHIC_PRESETS[classId];
        if (override) return override;
        const idx = Math.max(
          0,
          CHARACTER_CLASSES.findIndex((c: CharacterClassDef) => c.id === classId)
        );
        return { bodyId: CLASS_BODY_PLACEHOLDER[idx % CLASS_BODY_PLACEHOLDER.length] };
      };

      // =====================================================================
      // ESTRUCTURA fullscreen: nameBlock (arriba-centro) · leftZone (anclada izq) ·
      // centerZone (preview centrado) · rightZone (anclada der) · classRowWrap
      // (franja inferior) · bottomBar (acciones abajo). Cada zona se ancla en
      // absoluto contra los bordes del root.
      // =====================================================================

      // ---------------------------------------------------------------------
      // ZONA IZQUIERDA: razas + toggle de sexo. Panel anclado al borde izquierdo,
      // alto completo (top→bottom) y scrollable: en monitores bajos el contenido
      // scrollea en vez de recortarse. Transparente (el arte createchar de adentro
      // —raceContainer cc_small_window— ya aporta el marco).
      // ---------------------------------------------------------------------
      // Altura por contenido (no estirada): el `top` se calcula en recomputeScale()
      // para centrar verticalmente el bloque, y se aplica el MISMO top a ambos paneles
      // (y al título de nombre) para que RAZA/CABEZA/NOMBRE queden a la misma altura.
      const leftZone = document.createElement('div');
      leftZone.style.position = 'absolute';
      leftZone.style.left = '20px';
      // Altura completa (top→bottom) y SIN scrollbar: el contenido entra a lo alto y se
      // centra verticalmente (justify-content:center).
      leftZone.style.top = '22px';
      leftZone.style.bottom = '22px';
      leftZone.style.width = '280px';
      leftZone.style.boxSizing = 'border-box';
      leftZone.style.display = 'flex';
      leftZone.style.flexDirection = 'column';
      leftZone.style.alignItems = 'center';
      leftZone.style.justifyContent = 'center';
      leftZone.style.gap = '10px';
      leftZone.style.padding = '6px';
      leftZone.style.overflow = 'visible';

      const sectionTitle = (text: string): HTMLDivElement => {
        const d = document.createElement('div');
        d.textContent = text;
        d.style.fontSize = '12px';
        d.style.fontWeight = 'bold';
        d.style.letterSpacing = '1px';
        d.style.color = GOLD;
        d.style.textTransform = 'uppercase';
        d.style.textAlign = 'center';
        return d;
      };

      // Contorno que envuelve razas + sexos (marco cc_small_window, mismo patrón
      // border-image que las cajas de beneficio).
      const raceContainer = document.createElement('div');
      raceContainer.style.display = 'flex';
      raceContainer.style.flexDirection = 'column';
      raceContainer.style.alignItems = 'center';
      raceContainer.style.gap = '10px';
      raceContainer.style.boxSizing = 'border-box';
      raceContainer.style.borderStyle = 'solid';
      raceContainer.style.borderWidth = '16px';
      raceContainer.style.borderImageSource = `url('${ccUrl('cc_small_window')}')`;
      raceContainer.style.borderImageSlice = '26 fill';
      raceContainer.style.borderImageWidth = '16px';
      raceContainer.style.borderImageRepeat = 'stretch';
      raceContainer.style.borderColor = 'transparent';

      // El título RAZA va FUERA de la caja (primer hijo de leftZone), a la misma altura
      // que el título CABEZA (primer hijo de rightZone) — ver más abajo el append.

      const racesWrap = document.createElement('div');
      racesWrap.style.display = 'flex';
      racesWrap.style.flexDirection = 'column';
      racesWrap.style.gap = '8px';
      racesWrap.style.alignItems = 'center';

      interface CircleRef {
        id: string;
        tile: SkinnedTile;
        redraw: () => void;
      }
      const raceCircles: CircleRef[] = [];

      // Helper de círculo (sigue usándose para los iconos de clase de la fila inferior).
      const makeCircle = (size: number): HTMLDivElement => {
        const el = document.createElement('div');
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.borderRadius = '50%';
        el.style.overflow = 'hidden';
        el.style.background = '#1a1a1a';
        el.style.border = '2px solid #555';
        el.style.cursor = 'pointer';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.boxSizing = 'border-box';
        el.style.imageRendering = 'pixelated';
        return el;
      };

      CHARACTER_RACES.forEach((race: CharacterRaceDef) => {
        // Tile cuadrado skineado (arte ui/createchar) con la cabeza de la raza dentro.
        const tile = makeSkinnedTile(64, 'cc_race_class_icon_bg', 'cc_race_class_frame-SELECTED');
        const canvas = document.createElement('canvas');
        // Escala 2 (no 3): la cabeza (23×30 por frame → 46×60 a escala 2) entra
        // COMPLETA en el canvas de 62 sin recortarse en alto. A escala 3 (90px de alto)
        // se cortaba arriba/abajo y la cara se veía estirada/zoomeada.
        canvas.width = 62;
        canvas.height = 62;
        canvas.style.imageRendering = 'pixelated';
        // Bajar apenas la cabeza dentro del tile para que el pelo no pegue contra el
        // borde superior del marco. La cabeza (46×60 a escala 2) casi llena el canvas
        // de 62, así que el offset debe ser CHICO: 15px la bajaba de más y recortaba
        // el mentón (content tiene overflow:hidden). 4px sólo separa el pelo.
        canvas.style.transform = 'translateY(4px)';
        tile.content.appendChild(canvas);
        tile.el.title = race.labelEs;

        const redraw = (): void => {
          const headId = getAvailableHeadIds(race.id, selectedSex)[0];
          drawHeadCanvas(canvas, 2, headId, redraw);
        };

        tile.el.onclick = () => {
          if (selectedRace === race.id) return;
          selectedRace = race.id;
          currentHeadIndex = 0;
          updateRaceSelection();
          refreshHeads();
          refreshRaceInfo();
        };

        raceCircles.push({ id: race.id, tile, redraw });
        racesWrap.appendChild(tile.el);
      });

      raceContainer.appendChild(racesWrap);

      const updateRaceSelection = (): void => {
        raceCircles.forEach((c) => c.tile.setSelected(c.id === selectedRace));
      };

      // ----- Toggle de sexo (fila horizontal, tiles skineados) -----
      const sexWrap = document.createElement('div');
      sexWrap.style.display = 'flex';
      sexWrap.style.flexDirection = 'row';
      sexWrap.style.alignItems = 'center';
      sexWrap.style.justifyContent = 'center';
      sexWrap.style.gap = '10px';
      sexWrap.style.marginTop = '14px';

      const sexButtons: { id: string; tile: SkinnedTile }[] = [];
      CHARACTER_SEXES.forEach((sex: CharacterSexDef) => {
        const tile = makeSkinnedTile(52, 'cc_faction_bg_button', 'cc_faction_button-ACTIVE-SELECTED');
        tile.el.title = sex.labelEs;
        const icon = document.createElement('img');
        icon.src = ccUrl(sex.id === 'female' ? 'gender_icon_female' : 'gender_icon_male');
        icon.alt = sex.labelEs;
        icon.style.width = '68%';
        icon.style.height = '68%';
        icon.style.objectFit = 'contain';
        icon.style.imageRendering = 'pixelated';
        tile.content.appendChild(icon);
        tile.el.onclick = () => {
          if (selectedSex === sex.id) return;
          selectedSex = sex.id as CharacterSexId;
          currentHeadIndex = 0;
          updateSexSelection();
          raceCircles.forEach((c) => c.redraw()); // las caras cambian con el sexo
          refreshHeads();
        };
        sexButtons.push({ id: sex.id, tile });
        sexWrap.appendChild(tile.el);
      });

      const updateSexSelection = (): void => {
        sexButtons.forEach((s) => s.tile.setSelected(s.id === selectedSex));
      };

      raceContainer.appendChild(sexWrap);
      // Título RAZA afuera y arriba de la caja (alinea con CABEZA), luego la caja.
      leftZone.append(sectionTitle('Raza'), raceContainer);

      // ---------------------------------------------------------------------
      // BLOQUE DE NOMBRE (header centrado): título → input → párrafo de
      // aclaración. Anclado arriba-centro del root fullscreen, por encima del
      // muñeco (centerZone arranca debajo).
      // ---------------------------------------------------------------------
      const nameBlock = document.createElement('div');
      // Anclado arriba-centro, debajo del label de SITE_URL. width auto (lo define el
      // input de 420px); no afecta a las zonas laterales (que están ancladas a los bordes).
      nameBlock.style.position = 'absolute';
      nameBlock.style.top = '48px';
      nameBlock.style.left = '50%';
      nameBlock.style.transform = 'translateX(-50%)';
      nameBlock.style.zIndex = '6';
      nameBlock.style.display = 'flex';
      nameBlock.style.flexDirection = 'column';
      nameBlock.style.alignItems = 'center';
      nameBlock.style.gap = '2px';

      nameBlock.appendChild(sectionTitle('Nombre del personaje'));

      const nameField = createTextInput({
        placeholder: '',
        autocomplete: 'off',
      });
      const nameInput = nameField.input;
      nameInput.maxLength = 12;
      // Que el input reciba TODAS las teclas: cortar la propagación en capture phase para
      // que ninguna captura global (input de juego / Phaser keyboard) haga preventDefault
      // sobre teclas con función de juego (W/A/S/D, números, etc.) mientras se tipea el nombre.
      for (const ev of ['keydown', 'keyup', 'keypress'] as const) {
        nameInput.addEventListener(ev, (e) => e.stopPropagation(), true);
      }
      // Reskin con el arte ui/createchar: fill oscuro del input (cc_char_name_input).
      // El <input> real queda transparente encima (lógica/validación intactas).
      // Sin flourish detrás: leía como un "gráfico raro" suelto sobre el panel.
      const nameWrap = document.createElement('div');
      nameWrap.style.position = 'relative';
      nameWrap.style.width = '420px';
      nameWrap.style.height = '52px';
      nameField.wrapper.style.position = 'relative';
      nameField.wrapper.style.width = '100%';
      nameField.wrapper.style.height = '100%';
      nameField.wrapper.style.textAlign = 'center';
      nameField.wrapper.style.background = `url('${ccUrl('cc_char_name_input')}') center/100% 100% no-repeat`;
      nameField.wrapper.style.border = 'none';
      nameField.wrapper.style.boxShadow = 'none';
      nameField.wrapper.style.borderRadius = '0';
      nameInput.style.background = 'transparent';
      nameInput.style.border = 'none';
      nameInput.style.textAlign = 'center';
      nameInput.style.color = '#eee';
      nameWrap.appendChild(nameField.wrapper);

      const nameErrorLabel = document.createElement('div');
      nameErrorLabel.style.color = '#f55';
      nameErrorLabel.style.fontSize = '11px';
      nameErrorLabel.style.minHeight = '14px';
      nameErrorLabel.style.textAlign = 'center';

      // Aclaración de límites/excluyentes del nombre (acompaña sin robar
      // prioridad visual): text-xs, gris, máx. 3 líneas.
      const nameHint = document.createElement('div');
      // <br> antes de la última regla: cae en su propia línea, sin separación extra.
      nameHint.innerHTML =
        'Hasta 12 caracteres. Solo letras, números y espacios (máx. 2 números).<br>' +
        'No puede empezar ni terminar con espacio.';
      nameHint.style.maxWidth = '420px';
      nameHint.style.marginTop = '2px'; // pegado al input (ahorra altura)
      nameHint.style.fontSize = '11px';
      nameHint.style.lineHeight = '1.3';
      nameHint.style.color = '#999';
      nameHint.style.textAlign = 'center';
      nameHint.style.display = '-webkit-box';
      nameHint.style.setProperty('-webkit-line-clamp', '3');
      nameHint.style.setProperty('-webkit-box-orient', 'vertical');
      nameHint.style.overflow = 'hidden';

      // Orden: input → hint (pegado, ahorra altura) → error label (aparece sólo en error).
      nameBlock.append(nameWrap, nameHint, nameErrorLabel);

      // ---------------------------------------------------------------------
      // ZONA CENTRAL: preview + label in-game (el nombre vive en nameBlock)
      // ---------------------------------------------------------------------
      // Escenario central anclado (mismo patrón que centerStage del selector): ocupa
      // la franja vertical entre el nameBlock (arriba) y la franja de clases (abajo),
      // a todo el ancho, con el muñeco centrado. pointer-events none → no roba clicks
      // a las zonas laterales (que pasan por debajo a los costados). El nombre in-game
      // es hijo flex (no absoluto): como las zonas laterales ya no dependen del ancho
      // del centro, un nombre largo no mueve nada.
      const centerZone = document.createElement('div');
      centerZone.style.position = 'absolute';
      centerZone.style.top = '150px';
      centerZone.style.bottom = '150px';
      centerZone.style.left = '0';
      centerZone.style.right = '0';
      centerZone.style.display = 'flex';
      centerZone.style.flexDirection = 'column';
      centerZone.style.alignItems = 'center';
      centerZone.style.justifyContent = 'center';
      centerZone.style.gap = '0';
      centerZone.style.pointerEvents = 'none';
      // z-index bajo: el muñeco se dibuja POR DETRÁS de los círculos de clase (classRowWrap).
      centerZone.style.zIndex = '1';

      // Escenario del preview: glow cálido sutil + canvas (sombra de contacto en
      // el propio canvas, vía CharacterPreview).
      const previewStage = document.createElement('div');
      previewStage.style.position = 'relative';
      previewStage.style.display = 'flex';
      previewStage.style.alignItems = 'flex-end';
      previewStage.style.justifyContent = 'center';
      // Sin padding inferior: el nombre se pega a los pies vía marginTop negativo.
      previewStage.style.padding = '8px 0 0 0';
      // Subir el muñeco: el canvas tiene aire arriba de la cabeza (NATIVE_H=96, la
      // cabeza arranca ~y25) y el centerZone lo centra, así que sin esto queda bajo.
      // Aprovechamos el espacio libre entre el input de nombre y la cabeza.
      previewStage.style.transform = 'translateY(-40px)';

      // Canvas del preview del personaje, manejado por CharacterPreview (compone
      // body+head+equipo mirando abajo y hace la respiración in-game). En creación
      // no hay equipo todavía → muestra el cuerpo desnudo por raza/sexo + la cabeza.
      const previewCanvas = document.createElement('canvas');
      previewCanvas.style.position = 'relative';
      previewCanvas.style.zIndex = '1';
      const charPreview = new CharacterPreview(previewCanvas, { scale: PREVIEW_SCALE });

      previewStage.append(previewCanvas);

      // (El nombre YA NO se muestra debajo del muñeco; vive solo en el input de arriba.)

      const refreshPreview = (): void => {
        // Raza/sexo/cabeza + el preset gráfico de la clase elegida (body y, en el
        // futuro, weapon/shield/helm/boot). El cuerpo desnudo queda de fallback si
        // el preset no trae bodyId.
        charPreview.setAppearance({
          race: selectedRace,
          sex: selectedSex,
          headId: availableHeadIds[currentHeadIndex],
          ...classPreset(selectedClassId),
        });
      };

      // Recalcula SOLO la escala entera del muñeco (los paneles laterales van a altura
      // completa y sin scroll, así que ya no hay clamp ni centrado vertical por columnas).
      const recomputeScale = (): void => {
        const target = Math.max(280, Math.min(720, window.innerHeight - 220));
        const s = Math.max(3, Math.min(10, Math.floor(target / 96)));
        if (s !== PREVIEW_SCALE) {
          PREVIEW_SCALE = s;
          charPreview.setScale(s);
          refreshPreview();
        }
      };
      const onResize = (): void => recomputeScale();
      const cleanup = (): void => {
        window.removeEventListener('resize', onResize);
        charPreview.destroy();
      };

      nameInput.addEventListener('input', () => {
        const validation = validateCharacterName(nameInput.value);
        if (!validation.isValid && nameInput.value.length > 0) {
          nameErrorLabel.textContent = validation.error ?? '';
        } else {
          nameErrorLabel.textContent = '';
        }
      });

      centerZone.append(previewStage);

      // ---------------------------------------------------------------------
      // ZONA DERECHA: selector de cabeza + cajas de info. Panel anclado al borde
      // derecho, alto completo y scrollable (mismo criterio que la zona izquierda).
      // ---------------------------------------------------------------------
      const rightZone = document.createElement('div');
      rightZone.style.position = 'absolute';
      rightZone.style.right = '20px';
      // Altura completa (top→bottom) y SIN scrollbar; contenido centrado a lo alto.
      rightZone.style.top = '22px';
      rightZone.style.bottom = '22px';
      rightZone.style.width = '320px';
      rightZone.style.boxSizing = 'border-box';
      rightZone.style.display = 'flex';
      rightZone.style.flexDirection = 'column';
      rightZone.style.alignItems = 'center';
      rightZone.style.justifyContent = 'center';
      rightZone.style.gap = '12px';
      rightZone.style.padding = '6px';
      rightZone.style.overflow = 'visible';

      rightZone.appendChild(sectionTitle('Cabeza'));

      const headSelectorContainer = document.createElement('div');
      headSelectorContainer.style.display = 'flex';
      headSelectorContainer.style.alignItems = 'center';
      headSelectorContainer.style.justifyContent = 'center';
      headSelectorContainer.style.gap = '10px';
      headSelectorContainer.style.padding = '4px';

      // Flecha del selector: <img> del arte ui/createchar con swap a -HOVER al pasar.
      const arrowBtn = (base: string): HTMLImageElement => {
        const img = document.createElement('img');
        img.src = ccUrl(base);
        img.style.width = '34px';
        img.style.height = '34px';
        img.style.cursor = 'pointer';
        img.style.imageRendering = 'pixelated';
        img.onmouseenter = () => (img.src = ccUrl(`${base}-HOVER`));
        img.onmouseleave = () => (img.src = ccUrl(base));
        return img;
      };
      const headLeftArrow = arrowBtn('cc_char_options_arrow_left');
      const headRightArrow = arrowBtn('cc_char_options_arrow_right');

      const headPreviewCanvas = document.createElement('canvas');
      headPreviewCanvas.width = 23 * 3;
      headPreviewCanvas.height = 30 * 3;
      headPreviewCanvas.style.imageRendering = 'pixelated';
      headPreviewCanvas.style.display = 'block';

      // Box de la cabeza: marco completo (cc_small_window por border-image, con bordes
      // laterales visibles) + el canvas + el contador overlay anclado abajo (no suma alto).
      const headBox = document.createElement('div');
      headBox.style.position = 'relative';
      headBox.style.display = 'inline-flex';
      headBox.style.alignItems = 'center';
      headBox.style.justifyContent = 'center';
      headBox.style.padding = '6px 12px';
      headBox.style.boxSizing = 'border-box';
      headBox.style.borderStyle = 'solid';
      headBox.style.borderWidth = '12px';
      headBox.style.borderImageSource = `url('${ccUrl('cc_small_window')}')`;
      headBox.style.borderImageSlice = '26 fill';
      headBox.style.borderImageWidth = '12px';
      headBox.style.borderImageRepeat = 'stretch';
      headBox.style.borderColor = 'transparent';
      headBox.style.imageRendering = 'pixelated';

      const headIndexLabel = document.createElement('div');
      headIndexLabel.style.position = 'absolute';
      headIndexLabel.style.bottom = '2px';
      headIndexLabel.style.left = '0';
      headIndexLabel.style.right = '0';
      headIndexLabel.style.fontSize = '11px';
      headIndexLabel.style.color = '#ddd';
      headIndexLabel.style.textAlign = 'center';
      headIndexLabel.style.pointerEvents = 'none';
      headIndexLabel.style.textShadow = '1px 1px 0 #000';
      headBox.append(headPreviewCanvas, headIndexLabel);

      headSelectorContainer.append(headLeftArrow, headBox, headRightArrow);

      const refreshHeadPreview = (): void => {
        drawHeadCanvas(headPreviewCanvas, 3, availableHeadIds[currentHeadIndex], refreshHeadPreview);
        headIndexLabel.textContent =
          availableHeadIds.length === 0
            ? 'Sin cabezas'
            : `${currentHeadIndex + 1} / ${availableHeadIds.length}`;
      };

      // Recalcula las cabezas disponibles para raza/sexo y refresca todo lo dependiente.
      const refreshHeads = (): void => {
        availableHeadIds = getAvailableHeadIds(selectedRace, selectedSex);
        if (currentHeadIndex >= availableHeadIds.length) currentHeadIndex = 0;
        refreshHeadPreview();
        refreshPreview();
      };

      headLeftArrow.onclick = () => {
        if (availableHeadIds.length === 0) return;
        currentHeadIndex = (currentHeadIndex - 1 + availableHeadIds.length) % availableHeadIds.length;
        refreshHeadPreview();
        refreshPreview();
      };
      headRightArrow.onclick = () => {
        if (availableHeadIds.length === 0) return;
        currentHeadIndex = (currentHeadIndex + 1) % availableHeadIds.length;
        refreshHeadPreview();
        refreshPreview();
      };

      rightZone.append(headSelectorContainer);

      // ----- Cajas de info (skineadas con cc_small_window) -----
      // min-height reservado (título + 3 líneas de descripción/filas) para que
      // cambiar de raza/clase NO mueva el layout: el contenido más corto deja
      // espacio reservado en vez de encoger la caja.
      const INFO_BOX_MIN_HEIGHT = 200;

      // Botón "i" al pie de la caja → abre la wiki. Usa el arte ui/createchar con
      // swap a -HOVER/-PUSH. (Por ahora linkea a la home de la wiki; cuando existan
      // las páginas por raza/clase se apuntará a la específica.)
      const makeInfoButton = (): HTMLImageElement => {
        const btn = document.createElement('img');
        btn.src = ccUrl('cc_char_info_button');
        btn.title = 'Más información';
        // Anclado al borde INFERIOR de la caja (centrado, montado sobre el bisel), igual
        // en ambas cajas sin importar el alto del contenido. El `bottom` se mide desde el
        // borde interno (padding box); con el borde de 13px, -26px deja el botón montado
        // justo sobre la línea inferior del marco. El wrap es position:relative.
        btn.style.position = 'absolute';
        btn.style.bottom = '-26px';
        btn.style.left = '50%';
        btn.style.transform = 'translateX(-50%)';
        btn.style.width = '34px';
        btn.style.height = '34px';
        btn.style.cursor = 'pointer';
        btn.style.imageRendering = 'pixelated';
        btn.onmouseenter = () => (btn.src = ccUrl('cc_char_info_button-HOVER'));
        btn.onmouseleave = () => (btn.src = ccUrl('cc_char_info_button'));
        btn.onmousedown = () => (btn.src = ccUrl('cc_char_info_button-PUSH'));
        btn.onmouseup = () => (btn.src = ccUrl('cc_char_info_button-HOVER'));
        btn.onclick = () => window.open(WIKI_URL, '_blank', 'noopener');
        return btn;
      };

      // Caja skineada: marco cc_small_window (border-image, sin distorsión de
      // esquinas) + zona de contenido + botón "i". Si `withClassCircle`, agrega
      // arriba el círculo cc_char_info_icon con el icono de la clase elegida.
      interface SkinnedInfoBox {
        wrap: HTMLDivElement;
        content: HTMLDivElement;
        classIcon: HTMLImageElement | null;
      }
      const makeSkinnedInfoBox = (withClassCircle: boolean): SkinnedInfoBox => {
        const wrap = document.createElement('div');
        wrap.style.position = 'relative';
        wrap.style.display = 'flex';
        wrap.style.flexDirection = 'column';
        wrap.style.alignItems = 'center';
        wrap.style.boxSizing = 'border-box';
        wrap.style.borderStyle = 'solid';
        wrap.style.borderWidth = '13px';
        wrap.style.borderImageSource = `url('${ccUrl('cc_small_window')}')`;
        wrap.style.borderImageSlice = '26 fill';
        wrap.style.borderImageWidth = '13px';
        wrap.style.borderImageRepeat = 'stretch';
        wrap.style.borderColor = 'transparent';
        wrap.style.minHeight = `${INFO_BOX_MIN_HEIGHT}px`;
        // Padding generoso ADENTRO del marco: el bisel del arte es más ancho que el
        // border-width, así que sin esto el texto quedaba encimado al borde. El extra
        // abajo reserva lugar para el botón (i) anclado al borde inferior.
        wrap.style.padding = '12px 18px 22px';

        let classIcon: HTMLImageElement | null = null;
        if (withClassCircle) {
          // cc_char_info_icon es ancho (401×195: disco central + "alas" laterales);
          // se respeta su aspect ratio (alto fijo, ancho auto) para no aplastarlo, y
          // el icono de clase se centra sobre el disco negro (≈ alto del frame).
          const FRAME_H = 76;
          wrap.style.marginTop = `${Math.round(FRAME_H * 0.45)}px`; // aire para el sobresalto
          const circle = document.createElement('div');
          circle.style.position = 'relative';
          circle.style.height = `${FRAME_H}px`;
          circle.style.marginTop = `${-Math.round(FRAME_H * 0.7)}px`;
          circle.style.marginBottom = '6px';
          circle.style.display = 'flex';
          circle.style.alignItems = 'center';
          circle.style.justifyContent = 'center';
          const frameImg = document.createElement('img');
          frameImg.src = ccUrl('cc_char_info_icon');
          frameImg.alt = '';
          frameImg.style.height = '100%';
          frameImg.style.width = 'auto';
          frameImg.style.display = 'block';
          frameImg.style.imageRendering = 'pixelated';
          classIcon = document.createElement('img');
          classIcon.alt = '';
          classIcon.style.position = 'absolute';
          classIcon.style.top = '50%';
          classIcon.style.left = '50%';
          classIcon.style.transform = 'translate(-50%, -50%)';
          // El disco negro de cc_char_info_icon ≈ 0.78·alto del frame; el icono lo llena
          // dejando un anillo fino (antes quedaba chico y se veía el círculo más grande).
          classIcon.style.width = `${Math.round(FRAME_H * 0.72)}px`;
          classIcon.style.height = `${Math.round(FRAME_H * 0.72)}px`;
          classIcon.style.borderRadius = '50%';
          classIcon.style.objectFit = 'cover';
          classIcon.style.imageRendering = 'pixelated';
          circle.append(frameImg, classIcon);
          wrap.appendChild(circle);
        }

        const content = document.createElement('div');
        content.style.width = '100%';
        content.style.fontSize = '13px';
        content.style.color = '#ccc';
        content.style.lineHeight = '1.45';
        content.style.boxSizing = 'border-box';
        wrap.appendChild(content);

        wrap.appendChild(makeInfoButton());
        return { wrap, content, classIcon };
      };

      const classBox = makeSkinnedInfoBox(true);
      const raceBox = makeSkinnedInfoBox(false);
      const raceInfoBox = raceBox.content;
      const classInfoBox = classBox.content;
      const classIconImg = classBox.classIcon;

      // Renderiza una fila de modifier innato (raza/clase). Los flags únicos (ej.
      // "Voz del más allá") se muestran en dorado; los numéricos en verde/rojo.
      const appendModifierRow = (box: HTMLElement, row: ModifierDisplayRow): void => {
        const el = document.createElement('div');
        el.style.marginBottom = '3px';
        if (row.format === 'flag') {
          el.innerHTML = `<span style="color:${GOLD}">${row.label}</span>`;
        } else {
          const color = row.value >= 0 ? '#4ade80' : '#f87171';
          el.innerHTML = `${row.label}: <span style="color:${color}">${formatModifierValue(row)}</span>`;
        }
        box.appendChild(el);
      };

      const refreshRaceInfo = (): void => {
        const def = CHARACTER_RACES.find((r: CharacterRaceDef) => r.id === selectedRace);
        const rows = describeModifiers(getRaceInnateModifiers(selectedRace));
        raceInfoBox.innerHTML = '';
        const t = document.createElement('div');
        t.textContent = def?.labelEs ?? selectedRace;
        t.style.fontSize = '15px';
        t.style.fontWeight = 'bold';
        t.style.color = GOLD;
        t.style.marginBottom = '6px';
        raceInfoBox.appendChild(t);
        if (def?.descriptionEs) {
          const desc = document.createElement('div');
          desc.textContent = def.descriptionEs;
          desc.style.marginBottom = '6px';
          desc.style.color = '#cccccc';
          // Reserva alto fijo para que los stats de abajo no salten al cambiar de raza.
          desc.style.minHeight = `${Math.round(3 * 13 * 1.45)}px`;
          raceInfoBox.appendChild(desc);
        }
        if (rows.length === 0) {
          const none = document.createElement('div');
          none.textContent = 'Sin mejoras de raza';
          none.style.color = '#999';
          raceInfoBox.appendChild(none);
        } else {
          rows.forEach((row) => appendModifierRow(raceInfoBox, row));
        }
      };

      const refreshClassInfo = (): void => {
        const def = CHARACTER_CLASSES.find((c: CharacterClassDef) => c.id === selectedClassId);
        // Icono de la clase elegida en el círculo de la caja (mismo arte que la fila).
        if (classIconImg) classIconImg.src = `/assets/classes/${selectedClassId}.png`;
        classInfoBox.innerHTML = '';
        if (!def) return;
        const t = document.createElement('div');
        t.textContent = def.labelEs;
        t.style.fontSize = '15px';
        t.style.fontWeight = 'bold';
        t.style.color = GOLD;
        t.style.marginBottom = '6px';
        classInfoBox.appendChild(t);
        const desc = document.createElement('div');
        desc.textContent = def.descriptionEs;
        desc.style.marginBottom = '6px';
        // Reserva 3 líneas (13px·1.45) aunque la descripción sea más corta, así
        // los stats de abajo no saltan al cambiar de clase.
        desc.style.minHeight = `${Math.round(3 * 13 * 1.45)}px`;
        classInfoBox.appendChild(desc);
        const mkStat = (label: string, value: number, color: string): HTMLDivElement => {
          const row = document.createElement('div');
          row.style.marginTop = '2px';
          row.innerHTML = `<span style="color:${color}">${label}</span> ${value}`;
          return row;
        };
        classInfoBox.append(
          mkStat('HP', def.stats.baseHp, '#f87171'),
          mkStat('Maná', def.stats.baseMana, '#60a5fa'),
          mkStat('Energía', def.stats.baseStamina, '#fbbf24')
        );
        // Modificadores innatos de la clase (si los hay), incluyendo mods únicos.
        const classRows = describeModifiers(getClassInnateModifiers(selectedClassId));
        if (classRows.length > 0) {
          const sep = document.createElement('div');
          sep.style.marginTop = '6px';
          classInfoBox.appendChild(sep);
          classRows.forEach((row) => appendModifierRow(classInfoBox, row));
        }
      };

      // Orden del mockup: caja de clase (con círculo) arriba, caja de raza debajo.
      rightZone.append(classBox.wrap, raceBox.wrap);

      // ---------------------------------------------------------------------
      // FILA DE CLASES: franja horizontal anclada abajo (entre la barra de acciones
      // y el muñeco), a todo el ancho y centrada. Las dos filas de círculos hacen
      // wrap si la pantalla es muy angosta (no se cortan).
      // ---------------------------------------------------------------------
      const classRowWrap = document.createElement('div');
      classRowWrap.style.position = 'absolute';
      classRowWrap.style.left = '0';
      classRowWrap.style.right = '0';
      classRowWrap.style.bottom = '88px';
      // Por DELANTE del muñeco (centerZone z-index 1): los círculos de clase tapan al
      // preview cuando se solapan.
      classRowWrap.style.zIndex = '5';
      classRowWrap.style.display = 'flex';
      classRowWrap.style.flexDirection = 'column';
      classRowWrap.style.alignItems = 'center';
      classRowWrap.style.gap = '8px';

      // Grilla centrada: dos filas (10 + 9). flex-wrap: si la pantalla es muy angosta
      // los círculos bajan de línea en vez de cortarse/desbordar.
      const makeClassRow = (): HTMLDivElement => {
        const r = document.createElement('div');
        r.style.display = 'flex';
        r.style.flexWrap = 'wrap';
        r.style.justifyContent = 'center';
        r.style.gap = '10px';
        return r;
      };
      const classRow1 = makeClassRow();
      const classRow2 = makeClassRow();
      const classSplit = Math.ceil(CHARACTER_CLASSES.length / 2);

      const classCircles: { id: string; el: HTMLDivElement }[] = [];

      CHARACTER_CLASSES.forEach((cls: CharacterClassDef, idx: number) => {
        const item = document.createElement('div');
        item.style.display = 'flex';
        item.style.flexDirection = 'column';
        item.style.alignItems = 'center';
        item.style.width = '74px';
        item.style.cursor = 'pointer';

        const circle = makeCircle(60);
        const img = new Image();
        img.src = `/assets/classes/${cls.id}.png`;
        img.alt = cls.labelEs;
        img.style.width = '100%';
        img.style.height = '100%';
        img.style.objectFit = 'cover';
        img.onerror = () => {
          // Fallback: inicial de la clase para las que no tienen arte aún.
          img.style.display = 'none';
          circle.textContent = cls.labelEs.charAt(0).toUpperCase();
          circle.style.color = GOLD;
          circle.style.fontWeight = 'bold';
          circle.style.fontSize = '22px';
        };
        circle.appendChild(img);

        const lbl = document.createElement('div');
        lbl.textContent = cls.labelEs;
        lbl.style.fontSize = '11px';
        lbl.style.color = '#bbb';
        lbl.style.textAlign = 'center';
        lbl.style.marginTop = '3px';
        lbl.style.lineHeight = '1.1';

        item.onclick = () => {
          if (selectedClassId === cls.id) return;
          selectedClassId = cls.id;
          updateClassSelection();
          refreshPreview();
          refreshClassInfo();
        };

        item.append(circle, lbl);
        classCircles.push({ id: cls.id, el: circle });
        (idx < classSplit ? classRow1 : classRow2).appendChild(item);
      });

      const updateClassSelection = (): void => {
        classCircles.forEach((c) => {
          c.el.style.border = `2px solid ${c.id === selectedClassId ? GOLD : '#555'}`;
          c.el.style.boxShadow = c.id === selectedClassId ? `0 0 8px ${GOLD}` : 'none';
        });
      };

      classRowWrap.append(classRow1, classRow2);

      // ---------------------------------------------------------------------
      // BARRA DE ACCIONES: anclada abajo a todo el ancho (mismo patrón que el
      // bottomBar del selector). Volver (ghost, izq) · error de submit (centro) ·
      // Crear personaje (primary, der). Gradiente inferior para legibilidad.
      // ---------------------------------------------------------------------
      // Error de submit: centrado ARRIBA de la barra de acciones (el centro de la barra
      // ahora lo ocupa el logo). Anclado al root, aparece solo si hay error.
      const errorLabel = document.createElement('div');
      errorLabel.style.position = 'absolute';
      errorLabel.style.bottom = '84px';
      errorLabel.style.left = '50%';
      errorLabel.style.transform = 'translateX(-50%)';
      errorLabel.style.zIndex = '8';
      errorLabel.style.color = '#f55';
      errorLabel.style.minHeight = '18px';
      errorLabel.style.fontSize = '12px';
      errorLabel.style.textAlign = 'center';

      const actionRow = document.createElement('div');
      actionRow.style.position = 'absolute';
      actionRow.style.left = '0';
      actionRow.style.right = '0';
      actionRow.style.bottom = '0';
      actionRow.style.height = '80px';
      actionRow.style.display = 'flex';
      actionRow.style.alignItems = 'center';
      actionRow.style.justifyContent = 'space-between';
      actionRow.style.gap = '16px';
      actionRow.style.padding = '0 26px';
      actionRow.style.boxSizing = 'border-box';
      actionRow.style.background = 'linear-gradient(to top, rgba(0, 0, 0, 0.55), transparent)';

      // Misma estética que la pantalla de selección de personaje: createButton por
      // defecto. Crear personaje = primary (gold, resalta como "Entrar al personaje");
      // Volver = ghost (apagado). Sin skin custom de createchar (lo pidió el usuario).
      const submit = createButton('Crear personaje');
      const backButton = createButton('Volver', { variant: 'ghost' });
      submit.style.minWidth = '200px';
      backButton.style.minWidth = '200px';
      // Volver abajo-izquierda, Crear abajo-derecha (el error va aparte, sobre la barra;
      // el centro lo ocupa el logo).
      actionRow.append(backButton, submit);

      // =====================================================================
      // SUBMIT
      // =====================================================================
      submit.onclick = async () => {
        errorLabel.textContent = '';

        const name = nameInput.value; // NO usar trim(): preservar exactamente lo escrito
        const nameValidation = validateCharacterName(name);
        if (!nameValidation.isValid) {
          errorLabel.textContent = nameValidation.error ?? 'Nombre inválido';
          return;
        }

        if (availableHeadIds.length === 0) {
          errorLabel.textContent = 'No hay cabezas disponibles para esta combinación.';
          return;
        }
        const selectedHeadId = availableHeadIds[currentHeadIndex];

        submit.disabled = true;
        const result: CreateCharacterResult = await this.host.api.createCharacter(
          sessionToken,
          name,
          selectedClassId,
          selectedRace,
          selectedSex,
          selectedHeadId
        );
        submit.disabled = false;

        if (!result.success || !result.characterId) {
          errorLabel.textContent = result.error ?? 'No se pudo crear el personaje';
          return;
        }

        cleanup();
        resolve({
          id: result.characterId,
          name,
          level: 1,
          class: selectedClassId,
          race: selectedRace,
          sex: selectedSex,
          headId: selectedHeadId,
        });
      };

      backButton.onclick = async () => {
        cleanup();
        const chars = await this.host.api.getCharacters(sessionToken);
        const picked = await this.showCharacterPicker(sessionToken, chars);
        resolve(picked);
      };

      // =====================================================================
      // MONTAR: cada zona se ancla en absoluto contra los bordes del root fullscreen
      // (errorLabel vive dentro de actionRow, ver más arriba).
      // =====================================================================
      root.append(logo, nameBlock, centerZone, leftZone, rightZone, classRowWrap, errorLabel, actionRow);
      this.host.contentLayer.appendChild(root);

      // =====================================================================
      // INICIALIZACIÓN
      // =====================================================================
      updateRaceSelection();
      updateSexSelection();
      updateClassSelection();
      raceCircles.forEach((c) => c.redraw());
      refreshHeads();
      refreshRaceInfo();
      refreshClassInfo();
      recomputeScale();
      charPreview.start(); // arranca el loop de respiración
      window.addEventListener('resize', onResize);
      };
      void this.host.chrome.changeBackgroundForScreen('character_create', build);
    });
  }

  public async showCharacterPicker(
    sessionToken: string,
    characters: CharacterSummary[]
  ): Promise<CharacterSummary> {
    let currentSessionToken = sessionToken;
    let currentCharacters = characters;

    while (true) {
      const pickerResult = AuthUI.USE_SKINNED_CHARLIST
        ? await this.renderCharacterPickerScreenSkinned(currentSessionToken, currentCharacters)
        : await this.renderCharacterPickerScreen(currentSessionToken, currentCharacters);
      if (pickerResult.type === 'selected') {
        return pickerResult.character;
      }

      const newToken = await this.host.loginOrRegisterFlow();
      this.host.currentSessionToken = newToken;
      currentSessionToken = newToken;
      currentCharacters = await this.host.api.getCharacters(newToken);
    }
  }

  public async renderCharacterPickerScreen(
    sessionToken: string,
    characters: CharacterSummary[]
  ): Promise<CharacterPickerResult> {
    // Música de la fase de selección/login = intro (restaura tras volver de la creación).
    setLoadingPhaseMusic('music_intro');
    // Fundir directo al fondo del 1er personaje (que la lista auto-selecciona), no a un
    // pivot genérico: así NO hay doble fade (el `selectChar` inicial cae en el mismo
    // fondo → no-op). Sin personajes, al pivot de la pantalla de lista.
    const firstReal = characters.find((c): c is CharacterSummary => !!c);
    const entryBg = firstReal
      ? getBackgroundForCharacter(firstReal.mapId, firstReal.x, firstReal.y)
      : getBackgroundForScreen('character_list');
    await this.host.chrome.changeBackground(entryBg, { fullScreen: true });

    return new Promise<CharacterPickerResult>((resolve) => {
      this.host.contentLayer.innerHTML = '';

      const aureumLabel = document.createElement('div');
      aureumLabel.textContent = SITE_URL;
      aureumLabel.style.position = 'fixed';
      aureumLabel.style.top = '0';
      aureumLabel.style.left = '50%';
      aureumLabel.style.transform = 'translateX(-50%)';
      aureumLabel.style.padding = '12px';
      aureumLabel.style.color = '#eee';
      aureumLabel.style.fontSize = '14px';
      aureumLabel.style.fontFamily = 'system-ui, sans-serif';
      aureumLabel.style.zIndex = '1000';
      this.host.contentLayer.appendChild(aureumLabel);

      const GOLD = '#d9b25a';
      const onlineCharacterIds = new Set<string>();
      let intentionalDisconnect = false;

      // Personajes reales (el listado del server ya viene sin huecos).
      const realCharacters = characters.filter((c): c is CharacterSummary => !!c);
      let selectedId: string | null = null;

      interface RowRef {
        char: CharacterSummary;
        row: HTMLDivElement;
        statusEl: HTMLSpanElement;
      }
      const charRows: RowRef[] = [];
      // Reasignado al crear los botones (selectChar lo invoca al seleccionar).
      let updateActionButtons: () => void = () => {};

      // Helper para obtener el label de la raza
      const getRaceLabel = (raceId: string | undefined): string => {
        if (!raceId) return '';
        const race = CHARACTER_RACES.find((r: CharacterRaceDef) => r.id === raceId);
        return race ? race.labelEs : raceId;
      };

      // Helper para obtener el label de la clase
      const getClassLabel = (classId: string | undefined): string => {
        if (!classId) return '';
        const cls = CHARACTER_CLASSES.find((c: CharacterClassDef) => c.id === classId);
        return cls ? cls.labelEs : classId;
      };

      // =====================================================================
      // LAYOUT estilo WoW: logo arriba-izq, personaje grande al centro con su
      // nombre, lista a la derecha, barra de acciones abajo. Todo a pantalla
      // completa sobre el fondo de mapa (contentLayer).
      // =====================================================================
      const root = document.createElement('div');
      root.style.position = 'fixed';
      root.style.inset = '0';
      root.style.zIndex = '50';
      root.style.fontFamily = 'system-ui, sans-serif';
      root.style.color = '#eee';

      // --- Logo arriba-izquierda ---
      const logo = document.createElement('img');
      logo.alt = 'Aureum';
      logo.style.position = 'absolute';
      logo.style.top = '22px';
      logo.style.left = '26px';
      logo.style.width = '230px';
      logo.style.maxWidth = '32vw';
      logo.style.pointerEvents = 'none';
      logo.style.filter = LOGO_GLOW_FILTER;
      void this.host.chrome.getProcessedLogoUrl().then((u) => {
        logo.src = u;
      });

      // --- Escenario central: preview grande + nombre ---
      const centerStage = document.createElement('div');
      centerStage.style.position = 'absolute';
      centerStage.style.top = '0';
      centerStage.style.bottom = '92px';
      centerStage.style.left = '0';
      // Full-width: el muñeco + nombre quedan en el centro real de la pantalla,
      // alineados con el botón "Crear nuevo personaje" de la barra inferior.
      centerStage.style.right = '0';
      centerStage.style.display = 'flex';
      centerStage.style.flexDirection = 'column';
      centerStage.style.alignItems = 'center';
      centerStage.style.justifyContent = 'center';
      // gap 0: el nombre se pega a los pies vía marginTop negativo (recomputeScale),
      // igual que la distancia entidad↔label del juego.
      centerStage.style.gap = '0';
      centerStage.style.pointerEvents = 'none';

      const previewCanvas = document.createElement('canvas');
      previewCanvas.style.imageRendering = 'pixelated';
      const charPreview = new CharacterPreview(previewCanvas, { scale: 6 });
      let previewScale = 6;

      // Nombre del personaje seleccionado: estilo in-game (sin caja), escala con el
      // muñeco (12·escala) → misma relación fuente:cuerpo que en el juego.
      const centerName = document.createElement('div');
      centerName.style.color = '#fff';
      centerName.style.fontFamily = this.host.chrome.playerNamesFontFamily();
      centerName.style.lineHeight = '1';
      centerName.style.whiteSpace = 'nowrap';
      centerName.style.textAlign = 'center';

      const recomputeScale = (): void => {
        const target = Math.max(280, Math.min(720, window.innerHeight - 220));
        previewScale = Math.max(3, Math.min(10, Math.floor(target / 96)));
        charPreview.setScale(previewScale);
        centerName.style.fontSize = `${12 * previewScale}px`;
        centerName.style.textShadow = `${previewScale}px ${previewScale}px 0 #000`;
        centerName.style.minHeight = `${12 * previewScale}px`;
        // Pega el tope del nombre a los pies (borde inferior del cuerpo), igual
        // que la distancia entidad↔label in-game.
        centerName.style.marginTop = `${-charPreview.footToBottomPx}px`;
      };
      recomputeScale();

      centerStage.append(previewCanvas, centerName);

      // --- Lista de personajes a la derecha ---
      const listPanel = document.createElement('div');
      listPanel.style.position = 'absolute';
      listPanel.style.top = '22px';
      listPanel.style.right = '24px';
      listPanel.style.bottom = '92px';
      listPanel.style.width = '340px';
      listPanel.style.boxSizing = 'border-box';
      listPanel.style.display = 'flex';
      listPanel.style.flexDirection = 'column';
      listPanel.style.gap = '8px';
      listPanel.style.padding = '14px';
      listPanel.style.overflowY = 'auto';
      listPanel.style.background = 'rgba(10, 10, 12, 0.62)';
      listPanel.style.border = `1px solid ${GOLD}55`;
      listPanel.style.borderRadius = '10px';
      skinScrollable(listPanel);

      const listTitle = document.createElement('div');
      listTitle.textContent = 'Personajes';
      listTitle.style.fontSize = '13px';
      listTitle.style.fontWeight = 'bold';
      listTitle.style.letterSpacing = '1px';
      listTitle.style.textTransform = 'uppercase';
      listTitle.style.color = GOLD;
      listTitle.style.textAlign = 'center';
      listTitle.style.marginBottom = '4px';
      listPanel.appendChild(listTitle);

      // --- Ciclo de vida / limpieza ---
      const onResize = (): void => recomputeScale();
      // keepPreview=true: al entrar al juego dejamos el muñeco respirando durante
      // la carga (su destroy se difiere a AuthUI.destroy vía deferredPickerCleanup).
      const teardown = (keepPreview = false): void => {
        if (this.host.statusClient) {
          intentionalDisconnect = true;
          this.host.statusClient.leaveStatusRoom();
          this.host.statusClient = null;
        }
        if (!keepPreview) charPreview.destroy();
        window.removeEventListener('resize', onResize);
      };
      const finish = (result: CharacterPickerResult): void => {
        teardown();
        resolve(result);
      };

      // Pinta el highlight de la lista a partir de `selectedId` (única fuente de
      // verdad). Se llama tanto al seleccionar como al refrescar el estado online,
      // así el resalte nunca queda desincronizado con el preview central.
      const paintSelection = (): void => {
        for (const r of charRows) {
          const on = r.char.id === selectedId;
          r.row.style.background = on ? 'rgba(217, 178, 90, 0.18)' : 'rgba(255, 255, 255, 0.04)';
          r.row.style.borderColor = on ? GOLD : '#444';
        }
      };

      // Selecciona un personaje: highlight + preview central + nombre + fondo.
      const selectChar = (c: CharacterSummary): void => {
        selectedId = c.id;
        paintSelection();
        charPreview.setAppearance({
          race: c.race,
          sex: c.sex,
          headId: c.headId,
          bodyId: c.bodyId,
          nakedBodyId: c.nakedBodyId,
          weaponId: c.weaponId,
          shieldId: c.shieldId,
          helmId: c.helmId,
          bootId: c.bootId,
        });
        centerName.textContent = c.name;
        void this.host.chrome.changeBackgroundForCharacter(c);
        updateActionButtons();
      };

      // Entra al juego con el personaje seleccionado (si no está online). En vez de
      // tapar con una pantalla negra, se funde TODA la UI de selección (logo, lista,
      // barra de acciones, label del sitio y el nombre) y queda solo el personaje
      // (muñeco respirando) + una loading bar debajo. El juego carga por detrás;
      // main.ts alimenta la barra y, al estar listo, dispara el reveal.
      const enterGame = async (): Promise<void> => {
        if (this.host.transitionBar) return; // ya en transición (evita doble disparo)
        if (!selectedId) return;
        const c = realCharacters.find((x) => x.id === selectedId);
        if (!c || onlineCharacterIds.has(c.id)) return;
        // Bloquear toda interacción de la pantalla mientras carga el juego.
        root.style.pointerEvents = 'none';
        void this.host.chrome.changeBackgroundForCharacter(c);

        // Fade-out de TODA la UI menos el muñeco: logo, lista, barra de acciones,
        // label del sitio y el nombre del personaje.
        const FADE_MS = 400;
        const toHide: HTMLElement[] = [logo, listPanel, bottomBar, aureumLabel, centerName];
        for (const el of toHide) {
          el.style.transition = `opacity ${FADE_MS}ms ease-out`;
          el.style.opacity = '0';
        }

        // Loading bar anclada sobre el slot del nombre (sin mover el muñeco).
        this.host.chrome.showCharacterLoadingBar(centerStage, centerName);

        // Salida suave: dejar StatusRoom y quitar resize, PERO mantener el preview
        // respirando. Su destroy se difiere a AuthUI.destroy().
        teardown(true);
        this.host.deferredPickerCleanup = () => charPreview.destroy();
        resolve({ type: 'selected', character: c });
      };

      const HEAD_FRAME_WIDTH = 23;
      const HEAD_FRAME_HEIGHT = 30;
      const HEAD_SCALE = 1.8;
      const headSize = Math.round(HEAD_FRAME_WIDTH * HEAD_SCALE);

      for (const c of realCharacters) {
        const row = document.createElement('div');
        row.style.display = 'flex';
        row.style.alignItems = 'center';
        row.style.gap = '12px';
        row.style.padding = '12px';
        row.style.borderRadius = '6px';
        row.style.border = '1px solid #444';
        row.style.background = 'rgba(255, 255, 255, 0.04)';
        row.style.cursor = 'pointer';
        row.style.boxSizing = 'border-box';

        // Miniatura de la cara (frame DOWN, col 0 de la tira 1x4).
        const headThumb = document.createElement('div');
        headThumb.style.width = `${headSize}px`;
        headThumb.style.height = `${headSize}px`;
        headThumb.style.flexShrink = '0';
        headThumb.style.overflow = 'hidden';
        headThumb.style.imageRendering = 'pixelated';
        headThumb.style.backgroundSize = `${HEAD_FRAME_WIDTH * 4 * HEAD_SCALE}px ${HEAD_FRAME_HEIGHT * HEAD_SCALE}px`;
        headThumb.style.backgroundPosition = '0 0';
        if (c.headId) {
          headThumb.style.backgroundImage = `url('${getAssetUrl(`entities/head/head${c.headId}.png`)}')`;
        } else {
          headThumb.style.display = 'none';
        }

        const textCol = document.createElement('div');
        textCol.style.display = 'flex';
        textCol.style.flexDirection = 'column';
        textCol.style.flex = '1';
        textCol.style.minWidth = '0';

        const nameEl = document.createElement('span');
        nameEl.textContent = c.name;
        // Misma tipografía que el nombre in-game (pixel font Monogram, sin bold).
        nameEl.style.fontFamily = GAME_FONT_FAMILY;
        nameEl.style.fontSize = '14px';
        nameEl.style.whiteSpace = 'nowrap';
        nameEl.style.overflow = 'hidden';
        nameEl.style.textOverflow = 'ellipsis';

        const infoEl = document.createElement('span');
        const classLabel = getClassLabel(c.class) || c.class;
        const raceLabel = getRaceLabel(c.race);
        infoEl.textContent = `Nivel ${c.level} · ${raceLabel ? `${classLabel} ${raceLabel}` : classLabel}`;
        infoEl.style.fontSize = '12px';
        infoEl.style.opacity = '0.8';

        const statusEl = document.createElement('span');
        statusEl.style.fontSize = '12px';
        statusEl.style.flexShrink = '0';

        textCol.append(nameEl, infoEl);
        row.append(headThumb, textCol, statusEl);

        // 1er click selecciona; 2º click (sobre el ya seleccionado) entra. Online → no.
        row.onclick = () => {
          if (onlineCharacterIds.has(c.id)) return;
          if (selectedId === c.id) {
            void enterGame();
            return;
          }
          selectChar(c);
        };
        row.ondblclick = () => {
          if (onlineCharacterIds.has(c.id)) return;
          selectChar(c);
          void enterGame();
        };

        charRows.push({ char: c, row, statusEl });
        listPanel.appendChild(row);
      }

      // --- Fila "Crear nuevo personaje" (último ítem de la lista) ---
      // Se entiende como un botón, pero con MENOS prioridad visual que los
      // personajes: fondo más tenue, borde punteado, ícono "+" en gris.
      const atCharLimit = realCharacters.length >= AuthUI.MAX_CHARACTERS;
      const createRow = document.createElement('div');
      createRow.style.display = 'flex';
      createRow.style.alignItems = 'center';
      createRow.style.gap = '12px';
      createRow.style.padding = '12px';
      createRow.style.borderRadius = '6px';
      createRow.style.border = '1px dashed #555';
      createRow.style.background = 'rgba(255, 255, 255, 0.02)';
      createRow.style.boxSizing = 'border-box';
      createRow.style.cursor = atCharLimit ? 'default' : 'pointer';
      createRow.style.opacity = atCharLimit ? '0.4' : '0.85';

      const createIcon = document.createElement('div');
      createIcon.textContent = '+';
      createIcon.style.width = `${headSize}px`;
      createIcon.style.height = `${headSize}px`;
      createIcon.style.flexShrink = '0';
      createIcon.style.display = 'flex';
      createIcon.style.alignItems = 'center';
      createIcon.style.justifyContent = 'center';
      createIcon.style.fontSize = '24px';
      createIcon.style.fontWeight = 'bold';
      createIcon.style.color = '#888';
      createIcon.style.border = '1px dashed #555';
      createIcon.style.borderRadius = '6px';

      const createText = document.createElement('span');
      createText.textContent = atCharLimit
        ? `Máximo ${AuthUI.MAX_CHARACTERS} personajes`
        : 'Crear nuevo personaje';
      createText.style.fontSize = '13px';
      createText.style.color = '#aaa';

      createRow.append(createIcon, createText);

      if (!atCharLimit) {
        createRow.onmouseenter = (): void => {
          createRow.style.background = 'rgba(217, 178, 90, 0.10)';
          createRow.style.borderColor = `${GOLD}88`;
          createRow.style.opacity = '1';
        };
        createRow.onmouseleave = (): void => {
          createRow.style.background = 'rgba(255, 255, 255, 0.02)';
          createRow.style.borderColor = '#555';
          createRow.style.opacity = '0.85';
        };
        createRow.onclick = async (): Promise<void> => {
          const created = await this.showCreateCharacterForm(sessionToken);
          finish({ type: 'selected', character: created });
        };
      }

      listPanel.appendChild(createRow);

      // =====================================================================
      // BARRA DE ACCIONES (abajo): Volver/Opciones (izq) · Entrar al personaje
      // (centro, CTA ancho) · Eliminar (der).
      // =====================================================================
      const bottomBar = document.createElement('div');
      bottomBar.style.position = 'absolute';
      bottomBar.style.left = '0';
      bottomBar.style.right = '0';
      bottomBar.style.bottom = '0';
      bottomBar.style.height = '80px';
      // Grid 1fr·auto·1fr: la columna central (Entrar) queda en el centro REAL de
      // la pantalla (mismo eje que el muñeco), con las columnas laterales iguales.
      bottomBar.style.display = 'grid';
      bottomBar.style.gridTemplateColumns = '1fr auto 1fr';
      bottomBar.style.alignItems = 'center';
      bottomBar.style.padding = '0 26px';
      bottomBar.style.gap = '12px';
      bottomBar.style.boxSizing = 'border-box';
      bottomBar.style.background = 'linear-gradient(to top, rgba(0, 0, 0, 0.55), transparent)';

      // Izquierda: Volver (logout). Opciones ya no vive acá: el toggle de audio/opciones
      // se maneja in-game desde el menú de Opciones (ver docs/SOUND_AND_MUSIC.md).
      const leftGroup = document.createElement('div');
      leftGroup.style.display = 'flex';
      leftGroup.style.gap = '10px';
      leftGroup.style.justifySelf = 'start';
      const backButton = createButton('Volver', { variant: 'ghost' });
      backButton.onclick = async () => {
        const token = this.host.currentSessionToken;
        if (token) {
          try {
            await this.host.api.logout(token);
          } catch {
            // Ignorar errores de logout
          }
        }
        finish({ type: 'logout' });
      };
      leftGroup.append(backButton);

      // Centro: Entrar al personaje (CTA ancho; en el eje central, mismo que el
      // muñeco). Sólo activo con un personaje seleccionado.
      const enterButton = createButton('Entrar al personaje');
      enterButton.style.minWidth = '260px';
      enterButton.onclick = () => {
        void enterGame();
      };

      // Derecha: Eliminar (Crear nuevo personaje ahora vive como última fila de
      // la lista de personajes).
      const rightGroup = document.createElement('div');
      rightGroup.style.display = 'flex';
      rightGroup.style.gap = '10px';
      rightGroup.style.justifySelf = 'end';

      const deleteButton = createButton('Eliminar', { variant: 'ghost' });
      deleteButton.style.color = '#f08a8a';
      deleteButton.onclick = async () => {
        if (!selectedId) return;
        const c = realCharacters.find((x) => x.id === selectedId);
        if (!c || onlineCharacterIds.has(c.id)) return;
        const ok = await this.host.chrome.confirmDialog(
          `¿Eliminar a ${c.name}? Esta acción no se puede deshacer.`,
          'Eliminar personaje'
        );
        if (!ok) return;
        const res = await this.host.api.deleteCharacter(sessionToken, c.id);
        if (!res.success) {
          await this.host.chrome.confirmDialog(res.error ?? 'No se pudo eliminar el personaje.', 'Error', {
            okOnly: true,
          });
          return;
        }
        // Re-render con la lista actualizada, sin pasar por el login.
        teardown();
        const chars = await this.host.api.getCharacters(sessionToken);
        resolve(await this.renderCharacterPickerScreen(sessionToken, chars));
      };

      rightGroup.append(deleteButton);

      bottomBar.append(leftGroup, enterButton, rightGroup);

      // Entrar/Eliminar se activan sólo con un personaje seleccionado y offline.
      updateActionButtons = (): void => {
        const c = selectedId ? realCharacters.find((x) => x.id === selectedId) : null;
        const usable = !!c && !onlineCharacterIds.has(c.id);
        for (const btn of [enterButton, deleteButton]) {
          btn.disabled = !usable;
          btn.style.opacity = usable ? '1' : '0.5';
          btn.style.cursor = usable ? 'pointer' : 'default';
        }
      };

      root.append(logo, centerStage, listPanel, bottomBar);
      this.host.contentLayer.appendChild(root);

      // Estado online de cada personaje (badge + bloqueo de selección).
      const updateCharacterStatusUI = (): void => {
        for (const r of charRows) {
          const isOnline = onlineCharacterIds.has(r.char.id);
          r.statusEl.textContent = isOnline ? '🟢' : '';
          r.statusEl.title = isOnline ? 'Conectado' : '';
          r.row.style.opacity = isOnline ? '0.55' : '1';
          r.row.style.cursor = isOnline ? 'not-allowed' : 'pointer';
        }
        // Si el personaje seleccionado quedó online (no se puede entrar), mover la
        // selección al primer personaje offline disponible: así el resalte de la
        // lista y el preview central siguen mostrando el MISMO personaje. Si no hay
        // ninguno offline, se mantiene la selección actual resaltada (con los botones
        // deshabilitados por updateActionButtons), en vez de borrar el highlight y
        // dejar el preview huérfano.
        if (selectedId && onlineCharacterIds.has(selectedId)) {
          const firstOffline = realCharacters.find((c) => !onlineCharacterIds.has(c.id));
          if (firstOffline) {
            selectChar(firstOffline); // repinta highlight + preview + botones
            return;
          }
        }
        paintSelection();
        updateActionButtons();
      };

      // Selección inicial (primer personaje) para que el centro no quede vacío.
      if (realCharacters.length > 0) {
        selectChar(realCharacters[0]);
      } else {
        // Sin personajes creados: preview VACÍO (no un body por defecto sin cabeza).
        charPreview.setEmpty();
        centerName.textContent = '';
      }
      updateActionButtons();
      charPreview.start();
      window.addEventListener('resize', onResize);

      const statusClient = new ColyseusClient(this.host.serverUrl);
      this.host.statusClient = statusClient;

      statusClient
        .joinStatusRoom()
        .then(() => {
          let isFirstMessage = true;
          statusClient.onStatusMessage((message) => {
            if (message.type === ServerMessageType.CHARACTER_STATUS_UPDATE) {
              const statusMessage = message as CharacterStatusUpdateMessage;

              if (isFirstMessage) {
                onlineCharacterIds.clear();
                isFirstMessage = false;
                for (const characterId of statusMessage.characterIds) {
                  onlineCharacterIds.add(characterId);
                }
              } else {
                for (const characterId of statusMessage.characterIds) {
                  if (statusMessage.connected) {
                    onlineCharacterIds.add(characterId);
                  } else {
                    onlineCharacterIds.delete(characterId);
                  }
                }
              }

              updateCharacterStatusUI();
            }
          });

          statusClient.onStatusRoomDisconnect(() => {
            if (!intentionalDisconnect) {
              if (this.host.currentSessionToken) {
                void this.host.api.logout(this.host.currentSessionToken).catch(() => {
                  // Ignorar errores de logout
                });
              }
              window.location.reload();
            }
          });
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('[AuthUI] Failed to connect to status room:', error);
        });

    });
  }

  /**
   * Resuelve cuando todas las `<img>` dentro de `root` terminaron de cargar (o fallar),
   * para no revelar la pantalla con el arte (panel/botones/avatares) a medio pintar. Con
   * `timeoutMs` de guarda para no colgar el reveal si un asset nunca resuelve.
   */
  private awaitImagesLoaded(root: HTMLElement, timeoutMs = 4000): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img'));
    const all = Promise.all(
      imgs.map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) {
              resolve();
              return;
            }
            img.addEventListener('load', () => resolve(), { once: true });
            img.addEventListener('error', () => resolve(), { once: true });
          })
      )
    ).then(() => undefined);
    return Promise.race([all, new Promise<void>((r) => window.setTimeout(r, timeoutMs))]);
  }

  /**
   * Variante SKINEADA de la pantalla de selección (arte de `client/assets/ui/charlist`,
   * ver `docs/UI_SKINNING.md`). Misma orquestación/lógica que `renderCharacterPickerScreen`
   * (muñeco central vivo + nombre + fondo por personaje, StatusRoom online, crear/eliminar),
   * pero el panel de la lista usa el arte nuevo: contenedor `cs_container` a la izquierda,
   * header "SELECT REALM", filas skineadas con avatar circular, y botones CREATE CHARACTER /
   * PLAY con texto horneado. Volver/Opciones/Eliminar quedan en una barra slim abajo.
   * La versión original se conserva intacta; `AuthUI.USE_SKINNED_CHARLIST` elige cuál se usa.
   */
  public async renderCharacterPickerScreenSkinned(
    sessionToken: string,
    characters: CharacterSummary[]
  ): Promise<CharacterPickerResult> {
    // Música de la fase de selección/login = intro (restaura tras volver de la creación).
    setLoadingPhaseMusic('music_intro');
    const firstReal = characters.find((c): c is CharacterSummary => !!c);
    const entryBg = firstReal
      ? getBackgroundForCharacter(firstReal.mapId, firstReal.x, firstReal.y)
      : getBackgroundForScreen('character_list');

    return new Promise<CharacterPickerResult>((resolve) => {
      // Todo el contenido (lista skineada + muñeco) se arma DENTRO de `build`, que corre
      // MIENTRAS la pantalla está 100% en negro (whileHidden de changeBackground). Al final
      // `build` espera a que el muñeco y las imágenes de la UI estén cargados: así el
      // fade-out del negro NO arranca hasta que está TODO listo (antes se revelaba la
      // pantalla y el personaje aparecía después). Ver CharacterPreview.whenReady.
      const build = async (): Promise<void> => {
      this.host.contentLayer.innerHTML = '';

      // URL CDN-aware de un asset de la carpeta ui/charlist (key = archivo sin extensión).
      const clUrl = (file: string): string => getAssetUrl(`ui/charlist/${file}.png`);

      // Label "aureum.cc": oculto en esta pantalla (el logo, ahora centrado arriba, ya
      // aporta el branding y el label caía justo encima). Se mantiene el nodo por el
      // fundido de salida (toHide en enterGame).
      const aureumLabel = document.createElement('div');
      aureumLabel.textContent = SITE_URL;
      aureumLabel.style.display = 'none';
      this.host.contentLayer.appendChild(aureumLabel);

      // Ocultar también el link ESTÁTICO `#aureum-link` (index.html, z-index alto) que se
      // dibuja arriba-centro en todo el flujo de auth y quedaba sobre el logo del char-select.
      // Se restaura al salir (finish/enterGame) para no afectar login/create/juego.
      const staticSiteLink = document.getElementById('aureum-link');
      if (staticSiteLink) staticSiteLink.style.display = 'none';

      const GOLD = '#d9b25a';
      const onlineCharacterIds = new Set<string>();
      let intentionalDisconnect = false;

      const realCharacters = characters.filter((c): c is CharacterSummary => !!c);
      let selectedId: string | null = null;

      interface RowRef {
        char: CharacterSummary;
        row: HTMLDivElement;
        statusEl: HTMLSpanElement;
        deleteX: HTMLSpanElement;
        setSelected: (on: boolean) => void;
      }
      const charRows: RowRef[] = [];
      // Reasignado al crear los botones (selectChar lo invoca al seleccionar).
      let updateActionButtons: () => void = () => {};

      const getRaceLabel = (raceId: string | undefined): string => {
        if (!raceId) return '';
        const race = CHARACTER_RACES.find((r: CharacterRaceDef) => r.id === raceId);
        return race ? race.labelEs : raceId;
      };
      const getClassLabel = (classId: string | undefined): string => {
        if (!classId) return '';
        const cls = CHARACTER_CLASSES.find((c: CharacterClassDef) => c.id === classId);
        return cls ? cls.labelEs : classId;
      };

      // =====================================================================
      // LAYOUT: panel skineado a la IZQUIERDA (cs_container) con la lista + botones;
      // muñeco vivo + nombre centrados en el espacio a la derecha del panel (idéntico
      // a la versión original); barra slim de acciones secundarias abajo-derecha.
      // =====================================================================
      const root = document.createElement('div');
      root.style.position = 'fixed';
      root.style.inset = '0';
      root.style.zIndex = '50';
      root.style.fontFamily = 'system-ui, sans-serif';
      root.style.color = '#eee';

      // --- Logo arriba-CENTRO: el ángulo superior izquierdo ahora lo ocupa el panel de
      // Novedades, así que el logo se centra arriba (entre los dos paneles laterales),
      // dejando la composición balanceada.
      const logo = document.createElement('img');
      logo.alt = 'Aureum';
      logo.style.position = 'absolute';
      logo.style.top = '14px';
      logo.style.left = '50%';
      logo.style.transform = 'translateX(-50%)';
      // Mismo tamaño que el logo de las pantallas de loading (fuente única: loadingLayout).
      logo.style.width = loadingLogoWidthCss();
      logo.style.pointerEvents = 'none';
      logo.style.zIndex = '2';
      logo.style.filter = LOGO_GLOW_FILTER;
      void this.host.chrome.getProcessedLogoUrl().then((u) => {
        logo.src = u;
      });

      // --- Escenario central: preview grande + nombre (a la derecha del panel) ---
      const centerStage = document.createElement('div');
      centerStage.style.position = 'absolute';
      centerStage.style.top = '0';
      centerStage.style.bottom = '48px';
      // A todo el ancho: el muñeco (y el loading bar, que se monta acá dentro) queda
      // centrado en el CENTRO REAL de la pantalla, no en el hueco a la izquierda del
      // panel. pointer-events:none (más abajo) evita robar clicks aunque se solape con
      // el panel de la derecha.
      centerStage.style.left = '0';
      centerStage.style.right = '0';
      centerStage.style.display = 'flex';
      centerStage.style.flexDirection = 'column';
      centerStage.style.alignItems = 'center';
      centerStage.style.justifyContent = 'center';
      centerStage.style.gap = '0';
      centerStage.style.pointerEvents = 'none';

      const previewCanvas = document.createElement('canvas');
      previewCanvas.style.imageRendering = 'pixelated';
      const charPreview = new CharacterPreview(previewCanvas, { scale: 6 });
      let previewScale = 6;

      const centerName = document.createElement('div');
      centerName.style.color = '#fff';
      centerName.style.fontFamily = this.host.chrome.playerNamesFontFamily();
      centerName.style.lineHeight = '1';
      centerName.style.whiteSpace = 'nowrap';
      centerName.style.textAlign = 'center';

      const recomputeScale = (): void => {
        const target = Math.max(280, Math.min(720, window.innerHeight - 220));
        previewScale = Math.max(3, Math.min(10, Math.floor(target / 96)));
        charPreview.setScale(previewScale);
        centerName.style.fontSize = `${12 * previewScale}px`;
        centerName.style.textShadow = `${previewScale}px ${previewScale}px 0 #000`;
        centerName.style.minHeight = `${12 * previewScale}px`;
        centerName.style.marginTop = `${-charPreview.footToBottomPx}px`;
      };
      recomputeScale();

      centerStage.append(previewCanvas, centerName);

      // --- Panel skineado a la izquierda (cs_container, frame 9-slice) ---
      const panel = document.createElement('div');
      panel.style.position = 'absolute';
      panel.style.right = '24px';
      // Arranca al margen superior del logo y llega hasta ABAJO DE TODO (bottom:16), igual que
      // el panel de NOVEDADES de la izquierda (mismo top/bottom → ambos ocupan toda la altura).
      panel.style.top = '22px';
      panel.style.bottom = '16px';
      panel.style.width = '360px';
      panel.style.boxSizing = 'border-box';
      panel.style.display = 'flex';
      panel.style.flexDirection = 'column';
      panel.style.gap = '12px';
      panel.style.padding = '6px 8px 10px 8px';
      panel.style.zIndex = '1';
      panel.style.imageRendering = 'pixelated';
      panel.style.borderStyle = 'solid';
      panel.style.borderWidth = '22px';
      panel.style.borderImageSource = `url('${clUrl('cs_container')}')`;
      panel.style.borderImageSlice = '30 fill';
      panel.style.borderImageWidth = '22px';
      panel.style.borderImageRepeat = 'stretch';
      panel.style.borderColor = 'transparent';

      // Título de panel de TEXTO (mismo estilo para NOVEDADES y ELEGIR PERSONAJE): dorado,
      // centrado, con borde inferior. Reemplaza el header horneado `cs_select_realm_btn_es`
      // para que ambos paneles laterales tengan un título idéntico (a pedido del usuario).
      const makePanelTitle = (text: string): HTMLDivElement => {
        const t = document.createElement('div');
        t.textContent = text;
        t.style.flex = '0 0 auto';
        t.style.textAlign = 'center';
        t.style.color = '#f2d98a';
        t.style.fontFamily = GAME_FONT_FAMILY;
        t.style.fontSize = '22px';
        t.style.letterSpacing = '2px';
        t.style.padding = '2px 0 6px';
        t.style.borderBottom = `1px solid ${GOLD}44`;
        return t;
      };

      // Header "ELEGIR PERSONAJE" (texto, mismo estilo que NOVEDADES).
      const header = makePanelTitle('ELEGIR PERSONAJE');
      panel.appendChild(header);

      // Cuánto pisa el círculo del avatar sobre el borde IZQUIERDO del marco (estilo ref
      // Blizzard). Como listWrap tiene overflow-y:auto, no puede desbordar en X; el truco
      // es correr el scroller entero a la izquierda por esta cantidad (marginLeft negativo)
      // y recomponer el texto/CREAR sumándola a su padding/margen para que no se muevan.
      const AVATAR_FRAME_OVERHANG = 18;

      // Lista scrollable de personajes.
      const listWrap = document.createElement('div');
      listWrap.style.display = 'flex';
      listWrap.style.flexDirection = 'column';
      listWrap.style.gap = '6px';
      listWrap.style.flex = '1 1 auto';
      listWrap.style.overflowY = 'auto';
      listWrap.style.overflowX = 'hidden';
      // Padding simétrico: el canal donde asoma la mitad del avatar NO va acá (indentaría
      // también al botón CREAR/PLAY), sino como marginLeft SOLO en las filas de personaje
      // (ver ROW_LEFT_CHANNEL). Así CREAR/PLAY ocupan el ancho completo con aire parejo.
      listWrap.style.padding = '2px 6px';
      // Corre el scroller a la izquierda para que el avatar pise el borde del marco (ver
      // AVATAR_FRAME_OVERHANG); el desborde a la derecha se mantiene por el margin negativo.
      listWrap.style.marginLeft = `-${AVATAR_FRAME_OVERHANG}px`;
      // Espacio inferior para que la última fila / CREAR no queden tapadas por el PLAY, que
      // ahora flota sobre el borde inferior del panel (ver playWrap absoluto más abajo).
      listWrap.style.paddingBottom = '46px';
      skinScrollable(listWrap);
      panel.appendChild(listWrap);

      // --- Ciclo de vida / limpieza ---
      const onResize = (): void => recomputeScale();
      const teardown = (keepPreview = false): void => {
        if (this.host.statusClient) {
          intentionalDisconnect = true;
          this.host.statusClient.leaveStatusRoom();
          this.host.statusClient = null;
        }
        if (!keepPreview) charPreview.destroy();
        window.removeEventListener('resize', onResize);
      };
      const finish = (result: CharacterPickerResult): void => {
        if (staticSiteLink) staticSiteLink.style.display = '';
        teardown();
        resolve(result);
      };

      // Resalte de la fila seleccionada (togglea el overlay cs_character_row_SELECTED).
      const paintSelection = (): void => {
        for (const r of charRows) r.setSelected(r.char.id === selectedId);
      };

      const selectChar = (c: CharacterSummary): void => {
        selectedId = c.id;
        paintSelection();
        charPreview.setAppearance({
          race: c.race,
          sex: c.sex,
          headId: c.headId,
          bodyId: c.bodyId,
          nakedBodyId: c.nakedBodyId,
          weaponId: c.weaponId,
          shieldId: c.shieldId,
          helmId: c.helmId,
          bootId: c.bootId,
        });
        centerName.textContent = c.name;
        void this.host.chrome.changeBackgroundForCharacter(c);
        updateActionButtons();
      };

      // Entra al juego con el personaje seleccionado (si offline). Funde toda la UI
      // menos el muñeco + loading bar, igual que la versión original.
      const enterGame = async (): Promise<void> => {
        if (this.host.transitionBar) return;
        if (!selectedId) return;
        const c = realCharacters.find((x) => x.id === selectedId);
        if (!c || onlineCharacterIds.has(c.id)) return;
        if (staticSiteLink) staticSiteLink.style.display = '';
        root.style.pointerEvents = 'none';
        void this.host.chrome.changeBackgroundForCharacter(c);

        const FADE_MS = 400;
        // newsPanel se declara más abajo en el mismo scope; enterGame corre en un click
        // (post-render), así que la referencia por closure ya está inicializada.
        const toHide: HTMLElement[] = [logo, newsPanel, panel, leftBar, aureumLabel, centerName, playWrap];
        for (const el of toHide) {
          el.style.transition = `opacity ${FADE_MS}ms ease-out`;
          el.style.opacity = '0';
        }

        this.host.chrome.showCharacterLoadingBar(centerStage, centerName);

        teardown(true);
        this.host.deferredPickerCleanup = () => charPreview.destroy();
        resolve({ type: 'selected', character: c });
      };

      // Miniatura de cara del avatar (frame DOWN, col 0 de la tira 1x4) dibujada en un
      // canvas centrado, igual que las tiles de raza del form de creación
      // (drawHeadCanvas): encuadre preciso del retrato dentro del disco del avatar.
      const AV_SIZE = 60;
      const AV_HEAD_SCALE = 2.35;
      const headImgCache = new Map<string, HTMLImageElement>();
      const getReadyHead = (src: string, redraw: () => void): HTMLImageElement | null => {
        let img = headImgCache.get(src);
        if (!img) {
          img = new Image();
          headImgCache.set(src, img);
          img.src = src;
        }
        if (img.complete && img.naturalWidth > 0) return img;
        img.addEventListener('load', redraw, { once: true });
        return null;
      };
      const drawAvatarHead = (
        canvas: HTMLCanvasElement,
        headId: number | undefined,
        redraw: () => void
      ): void => {
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.imageSmoothingEnabled = false;
        if (headId === undefined) return;
        const img = getReadyHead(getAssetUrl(`entities/head/head${headId}.png`), redraw);
        if (!img) return;
        const fw = img.width / 4;
        const fh = img.height;
        const dw = fw * AV_HEAD_SCALE;
        const dh = fh * AV_HEAD_SCALE;
        const dx = Math.round((canvas.width - dw) / 2);
        // Baja un toque el retrato para centrar la CARA en el disco del socket (el
        // sprite tiene la cara en la parte alta → sin esto quedaba pegada arriba).
        const dy = Math.round((canvas.height - dh) / 2) + 2;
        ctx.drawImage(img, 0, 0, fw, fh, dx, dy, dw, dh);
      };

      // Canal a la izquierda de la tarjeta. Se calibra para CENTRAR el grupo
      // [avatar + tarjeta] en el listado: el avatar asoma AVATAR_POKE(44)px a la izquierda
      // de la tarjeta, y listWrap reserva ~20px a la derecha para la scrollbar. Con
      // paddingLeft(6)+canal-44 = 20 → margen izq = margen der (20px). Canal = 58.
      const ROW_LEFT_CHANNEL = 58;
      for (const c of realCharacters) {
        const row = document.createElement('div');
        row.style.position = 'relative';
        // Alto ≈ ratio nativo del frame cs_character_row_SELECTED (503×145 ≈ 3.5:1)
        // para que el socket redondo del avatar no se deforme al estirar.
        row.style.height = '84px';
        row.style.marginLeft = `${ROW_LEFT_CHANNEL}px`;
        row.style.flex = '0 0 auto';
        row.style.display = 'flex';
        row.style.alignItems = 'center';
        row.style.gap = '11px';
        row.style.padding = '0 14px 0 8px';
        row.style.boxSizing = 'border-box';
        row.style.cursor = 'pointer';
        row.style.background = `url('${clUrl('cs_character_row_bg')}') center/100% 100% no-repeat`;
        row.style.imageRendering = 'pixelated';

        // Overlays de estado (absolutos, sin z-index → detrás del contenido z:1).
        const hoverOv = document.createElement('img');
        hoverOv.src = clUrl('cs_character_row_avatar-HOVER');
        hoverOv.style.position = 'absolute';
        hoverOv.style.inset = '0';
        hoverOv.style.width = '100%';
        hoverOv.style.height = '100%';
        hoverOv.style.pointerEvents = 'none';
        hoverOv.style.imageRendering = 'pixelated';
        hoverOv.style.display = 'none';

        const selOv = document.createElement('img');
        selOv.src = clUrl('cs_character_row_SELECTED');
        selOv.style.position = 'absolute';
        selOv.style.inset = '0';
        selOv.style.width = '100%';
        selOv.style.height = '100%';
        selOv.style.pointerEvents = 'none';
        selOv.style.imageRendering = 'pixelated';
        selOv.style.display = 'none';

        // Contenido (texto + estado) por encima de los overlays. El avatar NO va acá:
        // se ancla aparte al borde izquierdo de la fila (ver más abajo), así que el
        // texto arranca con padding-left para despejar la mitad derecha del disco.
        const content = document.createElement('div');
        content.style.position = 'relative';
        content.style.zIndex = '1';
        content.style.display = 'flex';
        content.style.alignItems = 'center';
        content.style.gap = '12px';
        content.style.width = '100%';
        content.style.minWidth = '0';
        // 34 base + el overhang del scroller para que el nombre no se corra al haber movido
        // listWrap a la izquierda (el avatar sí se corre, anclado al borde de la fila).
        content.style.paddingLeft = `${34 + AVATAR_FRAME_OVERHANG}px`;

        // Avatar circular: marco cs_character_row_avatar + cara + anillo overlay.
        // Anclado con su CENTRO sobre el borde izquierdo de la fila (left:0 +
        // translateX(-50%)): la mitad del disco asoma hacia el canal de listWrap y la
        // curva del marco de la fila (socket) lo envuelve. Va sobre todo (zIndex 3).
        const avatar = document.createElement('div');
        avatar.style.position = 'absolute';
        // Corrido hacia afuera del borde izquierdo de la fila: el disco asoma en el
        // canal (ROW_LEFT_CHANNEL) siguiendo la curva del marco (socket). left:-14 lo
        // lleva al máximo antes de que el overflow-x:hidden de listWrap lo recorte.
        avatar.style.left = '-14px';
        avatar.style.top = '50%';
        avatar.style.transform = 'translate(-50%, -50%)';
        avatar.style.zIndex = '3';
        avatar.style.width = `${AV_SIZE}px`;
        avatar.style.height = `${AV_SIZE}px`;
        avatar.style.display = 'flex';
        avatar.style.alignItems = 'center';
        avatar.style.justifyContent = 'center';
        avatar.style.background = `url('${clUrl('cs_character_row_avatar')}') center/100% 100% no-repeat`;
        avatar.style.imageRendering = 'pixelated';

        // Recorte circular para que el retrato quede contenido dentro del disco del
        // avatar (sin invadir el anillo). El fondo transparente del sprite deja ver
        // el disco oscuro del arte cs_character_row_avatar por detrás.
        const headClip = document.createElement('div');
        headClip.style.position = 'absolute';
        headClip.style.left = '50%';
        headClip.style.top = '50%';
        headClip.style.width = `${AV_SIZE - 4}px`;
        headClip.style.height = `${AV_SIZE - 4}px`;
        headClip.style.transform = 'translate(-50%, -50%)';
        headClip.style.borderRadius = '50%';
        headClip.style.overflow = 'hidden';
        headClip.style.pointerEvents = 'none';

        const headCanvas = document.createElement('canvas');
        headCanvas.width = AV_SIZE;
        headCanvas.height = AV_SIZE;
        headCanvas.style.position = 'absolute';
        headCanvas.style.left = '50%';
        headCanvas.style.top = '50%';
        headCanvas.style.width = `${AV_SIZE}px`;
        headCanvas.style.height = `${AV_SIZE}px`;
        headCanvas.style.transform = 'translate(-50%, -50%)';
        headCanvas.style.imageRendering = 'pixelated';
        if (c.headId) {
          const redrawHead = (): void => drawAvatarHead(headCanvas, c.headId, redrawHead);
          redrawHead();
        }
        headClip.appendChild(headCanvas);
        avatar.appendChild(headClip);

        const ring = document.createElement('img');
        ring.src = clUrl('cs_character_row_avatar_OVERLAY');
        ring.style.position = 'absolute';
        ring.style.inset = '0';
        ring.style.width = '100%';
        ring.style.height = '100%';
        ring.style.pointerEvents = 'none';
        ring.style.imageRendering = 'pixelated';
        avatar.appendChild(ring);

        // --- × de borrado: sutil, esquina superior izquierda de la fila. Aparece solo al
        // hacer hover sobre el box (y solo si el personaje está offline). Abre un modal de
        // confirmación y, con OK, borra y re-renderiza. Reutiliza el flujo existente.
        const deleteX = document.createElement('span');
        deleteX.textContent = '×';
        deleteX.title = 'Eliminar personaje';
        deleteX.style.position = 'absolute';
        deleteX.style.top = '3px';
        deleteX.style.left = '9px';
        deleteX.style.zIndex = '4';
        deleteX.style.fontFamily = GAME_FONT_FAMILY;
        deleteX.style.fontSize = '15px';
        deleteX.style.lineHeight = '1';
        deleteX.style.color = 'rgba(244,236,216,0.55)';
        deleteX.style.textShadow = '1px 1px 2px rgba(0,0,0,0.8)';
        deleteX.style.cursor = 'pointer';
        deleteX.style.opacity = '0';
        deleteX.style.transition = 'opacity 0.12s ease, color 0.12s ease';
        deleteX.style.pointerEvents = 'none'; // se habilita al revelarse en hover
        deleteX.onmouseenter = (): void => {
          deleteX.style.color = '#f08a8a';
        };
        deleteX.onmouseleave = (): void => {
          deleteX.style.color = 'rgba(244,236,216,0.55)';
        };
        deleteX.onclick = (ev: MouseEvent): void => {
          ev.stopPropagation(); // no seleccionar/entrar al personaje
          if (onlineCharacterIds.has(c.id)) return;
          void (async (): Promise<void> => {
            const ok = await this.host.chrome.confirmDialog(
              `¿Eliminar a ${c.name}? Esta acción no se puede deshacer.`,
              'Eliminar personaje'
            );
            if (!ok) return;
            const res = await this.host.api.deleteCharacter(sessionToken, c.id);
            if (!res.success) {
              await this.host.chrome.confirmDialog(
                res.error ?? 'No se pudo eliminar el personaje.',
                'Error',
                { okOnly: true }
              );
              return;
            }
            teardown();
            const chars = await this.host.api.getCharacters(sessionToken);
            resolve(await this.renderCharacterPickerScreenSkinned(sessionToken, chars));
          })();
        };

        const textCol = document.createElement('div');
        textCol.style.display = 'flex';
        textCol.style.flexDirection = 'column';
        textCol.style.flex = '1';
        textCol.style.minWidth = '0';
        textCol.style.gap = '2px';

        const nameEl = document.createElement('span');
        nameEl.textContent = c.name;
        // Misma tipografía que el nombre in-game (pixel font Monogram). Sin bold:
        // Monogram tiene un solo peso; el faux-bold la arruina.
        nameEl.style.fontFamily = GAME_FONT_FAMILY;
        nameEl.style.fontSize = '24px';
        nameEl.style.color = '#f4ecd8';
        nameEl.style.whiteSpace = 'nowrap';
        nameEl.style.overflow = 'hidden';
        nameEl.style.textOverflow = 'ellipsis';
        nameEl.style.textShadow = '1px 1px 2px rgba(0,0,0,0.8)';

        const infoEl = document.createElement('span');
        const classLabel = getClassLabel(c.class) || c.class;
        const raceLabel = getRaceLabel(c.race);
        infoEl.textContent = `${raceLabel ? `${classLabel} ${raceLabel}` : classLabel} · Nivel ${c.level}`;
        infoEl.style.fontSize = '12px';
        infoEl.style.color = GOLD;
        infoEl.style.opacity = '0.85';
        infoEl.style.whiteSpace = 'nowrap';
        infoEl.style.overflow = 'hidden';
        infoEl.style.textOverflow = 'ellipsis';

        const statusEl = document.createElement('span');
        statusEl.style.fontSize = '12px';
        statusEl.style.flexShrink = '0';

        textCol.append(nameEl, infoEl);
        content.append(textCol, statusEl);
        // El avatar va como hijo directo de la fila (relative) para anclarse a su borde
        // izquierdo; por encima de overlays y contenido (zIndex 3). La × va al final para
        // quedar por encima de todo (zIndex 4).
        row.append(hoverOv, selOv, content, avatar, deleteX);

        row.onmouseenter = (): void => {
          if (onlineCharacterIds.has(c.id)) return;
          hoverOv.style.display = 'block';
          deleteX.style.opacity = '1';
          deleteX.style.pointerEvents = 'auto';
        };
        row.onmouseleave = (): void => {
          hoverOv.style.display = 'none';
          deleteX.style.opacity = '0';
          deleteX.style.pointerEvents = 'none';
        };

        // 1er click selecciona; 2º click (sobre el ya seleccionado) entra. Online → no.
        row.onclick = (): void => {
          if (onlineCharacterIds.has(c.id)) return;
          if (selectedId === c.id) {
            void enterGame();
            return;
          }
          selectChar(c);
        };
        row.ondblclick = (): void => {
          if (onlineCharacterIds.has(c.id)) return;
          selectChar(c);
          void enterGame();
        };

        charRows.push({
          char: c,
          row,
          statusEl,
          deleteX,
          setSelected: (on: boolean): void => {
            selOv.style.display = on ? 'block' : 'none';
          },
        });
        listWrap.appendChild(row);
      }

      // --- Botón CREAR PERSONAJE: va como hijo DIRECTO del panel (debajo de la lista
      // scrollable, arriba de PLAY) para ocupar el ANCHO COMPLETO del panel igual que el
      // header "ELEGIR PERSONAJE". Dentro de listWrap quedaba angosto a la derecha por la
      // reserva de la scrollbar (~20px). Botón DOM temático (dorado sobre oscuro, fuente
      // pixel) en vez del arte cs_create_character_btn_es (pensado para fondo claro).
      const atCharLimit = realCharacters.length >= AuthUI.MAX_CHARACTERS;
      const createBtnEl = document.createElement('div');
      createBtnEl.style.flex = '0 0 auto';
      createBtnEl.style.marginTop = '6px';
      // marginLeft = ROW_LEFT_CHANNEL(58) − poke del avatar(44) = 14: el botón queda con el
      // MISMO extent horizontal que el grupo [avatar-izq … tarjeta-der] y estira a la derecha.
      // +overhang para compensar el marginLeft negativo de listWrap (que no debe correr CREAR).
      createBtnEl.style.marginLeft = `${14 + AVATAR_FRAME_OVERHANG}px`;
      createBtnEl.style.display = 'flex';
      createBtnEl.style.alignItems = 'center';
      createBtnEl.style.justifyContent = 'center';
      createBtnEl.style.gap = '10px';
      createBtnEl.style.height = '46px';
      createBtnEl.style.boxSizing = 'border-box';
      createBtnEl.style.background = 'rgba(0,0,0,0.34)';
      createBtnEl.style.border = `1px solid ${GOLD}66`;
      createBtnEl.style.borderRadius = '6px';
      createBtnEl.style.cursor = atCharLimit ? 'default' : 'pointer';
      createBtnEl.style.opacity = atCharLimit ? '0.45' : '1';
      createBtnEl.style.transition = 'background 0.12s ease, border-color 0.12s ease';
      createBtnEl.title = atCharLimit ? `Máximo ${AuthUI.MAX_CHARACTERS} personajes` : '';

      const createPlus = document.createElement('span');
      createPlus.textContent = '+';
      createPlus.style.fontFamily = GAME_FONT_FAMILY;
      createPlus.style.fontSize = '20px';
      createPlus.style.color = GOLD;
      createPlus.style.lineHeight = '1';
      // El glyph del pixel-font asienta un toque alto en su celda; un nudge óptico chico
      // lo centra con las mayúsculas de "CREAR PERSONAJE".
      createPlus.style.transform = 'translateY(1px)';
      const createLbl = document.createElement('span');
      createLbl.textContent = 'CREAR PERSONAJE';
      createLbl.style.fontFamily = GAME_FONT_FAMILY;
      createLbl.style.fontSize = '19px';
      createLbl.style.lineHeight = '1';
      createLbl.style.letterSpacing = '1px';
      createLbl.style.color = '#f0d68c';
      createBtnEl.append(createPlus, createLbl);

      if (!atCharLimit) {
        createBtnEl.onmouseenter = (): void => {
          createBtnEl.style.background = 'rgba(217,178,90,0.14)';
          createBtnEl.style.borderColor = `${GOLD}`;
        };
        createBtnEl.onmouseleave = (): void => {
          createBtnEl.style.background = 'rgba(0,0,0,0.34)';
          createBtnEl.style.borderColor = `${GOLD}66`;
        };
        createBtnEl.onclick = (): void => {
          void (async (): Promise<void> => {
            const created = await this.showCreateCharacterForm(sessionToken);
            finish({ type: 'selected', character: created });
          })();
        };
      }
      // Debajo de las filas, dentro de listWrap (no al fondo del panel).
      listWrap.appendChild(createBtnEl);

      // --- Botón PLAY (entra al juego) ---
      // Va CENTRADO en la pantalla (eje horizontal), en la parte INFERIOR. Se
      // cuelga del `root` (fixed inset:0, mismo z-stack que el resto) y se agrega
      // ÚLTIMO en root.append para quedar por ENCIMA y recibir el click. Sin la
      // bandeja de fondo: floja sobre fondo transparente dejaba un borde raro a
      // los costados. left:50% + translateX(-50%) lo centra en el ancho real.
      const playWrap = document.createElement('div');
      playWrap.style.position = 'absolute';
      playWrap.style.left = '50%';
      playWrap.style.bottom = '24px';
      playWrap.style.transform = 'translateX(-50%)';
      playWrap.style.width = '300px';
      playWrap.style.display = 'flex';
      playWrap.style.alignItems = 'center';
      playWrap.style.justifyContent = 'center';
      playWrap.style.zIndex = '4'; // arriba de panel(1)/leftBar(2) en su zona

      // Botón "Jugar" con la MISMA estética que el "Entrar" del login (createButton primary
      // dorado, fullWidth), a pedido del usuario: se abandona el arte horneado
      // `cs_button_play_es` (makeSkinnedButton) para que el CTA sea consistente con el resto
      // de la UI de auth.
      const playBtn = createButton('Jugar', { fullWidth: true });
      playBtn.onclick = () => {
        void enterGame();
      };
      playWrap.appendChild(playBtn);
      // El append al `root` va más abajo (junto al resto), ÚLTIMO, para que quede
      // arriba de todo y sea clickeable.

      // =====================================================================
      // Acciones secundarias: Volver abajo-IZQUIERDA, pegado al borde DERECHO del panel de
      // Novedades (que ahora llega hasta abajo de todo). left = 24 + 360 (ancho del panel) + 8
      // de aire. El borrado vive ahora como × por box.
      // =====================================================================
      const leftBar = document.createElement('div');
      leftBar.style.position = 'absolute';
      leftBar.style.left = '392px';
      leftBar.style.bottom = '16px';
      leftBar.style.display = 'flex';
      leftBar.style.gap = '10px';
      leftBar.style.alignItems = 'center';
      leftBar.style.zIndex = '2';

      // Antepone un ícono pixel al label de un botón ghost (reusa assets existentes).
      // `iconUrl` ya CDN-aware (getAssetUrl/clUrl). El <img> va pixelado y alineado
      // al texto; se preserva el label envolviéndolo en un <span>.
      const addButtonIcon = (btn: HTMLButtonElement, iconUrl: string): void => {
        const label = btn.textContent ?? '';
        btn.textContent = '';
        btn.style.display = 'inline-flex';
        btn.style.alignItems = 'center';
        btn.style.gap = '7px';
        const icon = document.createElement('img');
        icon.src = iconUrl;
        icon.alt = '';
        icon.style.width = '16px';
        icon.style.height = '16px';
        icon.style.objectFit = 'contain';
        icon.style.imageRendering = 'pixelated';
        icon.style.pointerEvents = 'none';
        const span = document.createElement('span');
        span.textContent = label;
        btn.append(icon, span);
      };

      const backButton = createButton('Volver', { variant: 'ghost' });
      backButton.onclick = async () => {
        const token = this.host.currentSessionToken;
        if (token) {
          try {
            await this.host.api.logout(token);
          } catch {
            // Ignorar errores de logout
          }
        }
        finish({ type: 'logout' });
      };

      // El borrado ya no vive en la barra inferior: ahora es la × por box (hover) de cada
      // fila (ver el loop de personajes arriba).

      // Íconos pixel (reuso de assets existentes).
      addButtonIcon(backButton, getAssetUrl('ui/createchar/cc_char_options_arrow_left.png'));

      leftBar.append(backButton);

      // PLAY activo sólo con un personaje seleccionado y offline.
      updateActionButtons = (): void => {
        const c = selectedId ? realCharacters.find((x) => x.id === selectedId) : null;
        const usable = !!c && !onlineCharacterIds.has(c.id);
        playBtn.disabled = !usable;
      };

      // --- Panel de NOVEDADES (changelog) a la IZQUIERDA: espejo compacto del panel de
      // personajes de la derecha, para balancear la pantalla (noticias-izq · muñeco ·
      // lista-der). Mismo marco cs_container; header de texto + cuerpo del changelog
      // (buildChangelogBody(true) = fuentes chicas). Reemplaza al viejo modal del login.
      const newsPanel = document.createElement('div');
      newsPanel.style.position = 'absolute';
      newsPanel.style.left = '24px';
      newsPanel.style.top = '22px';
      // Misma altura que el panel de personajes (mismo top/bottom): ambos llegan hasta abajo de
      // todo. El botón Volver ya no cuelga debajo, sino pegado al borde DERECHO de este panel.
      newsPanel.style.bottom = '16px';
      newsPanel.style.width = '360px';
      newsPanel.style.boxSizing = 'border-box';
      newsPanel.style.display = 'flex';
      newsPanel.style.flexDirection = 'column';
      newsPanel.style.gap = '8px';
      newsPanel.style.padding = '6px 8px 12px 8px';
      newsPanel.style.zIndex = '1';
      newsPanel.style.imageRendering = 'pixelated';
      newsPanel.style.borderStyle = 'solid';
      newsPanel.style.borderWidth = '22px';
      newsPanel.style.borderImageSource = `url('${clUrl('cs_container')}')`;
      newsPanel.style.borderImageSlice = '30 fill';
      newsPanel.style.borderImageWidth = '22px';
      newsPanel.style.borderImageRepeat = 'stretch';
      newsPanel.style.borderColor = 'transparent';

      const newsTitle = makePanelTitle('NOVEDADES');

      const newsBody = this.host.buildChangelogBody(true);
      newsBody.style.flex = '1 1 auto';
      newsBody.style.minHeight = '0';
      newsPanel.append(newsTitle, newsBody);

      root.append(logo, centerStage, newsPanel, panel, leftBar, playWrap);
      this.host.contentLayer.appendChild(root);

      // Estado online de cada personaje (badge + bloqueo de selección).
      const updateCharacterStatusUI = (): void => {
        for (const r of charRows) {
          const isOnline = onlineCharacterIds.has(r.char.id);
          r.statusEl.textContent = isOnline ? '🟢' : '';
          r.statusEl.title = isOnline ? 'Conectado' : '';
          r.row.style.opacity = isOnline ? '0.55' : '1';
          r.row.style.cursor = isOnline ? 'not-allowed' : 'pointer';
          // Personaje online → no se puede borrar: ocultar la × y desactivarla.
          if (isOnline) {
            r.deleteX.style.display = 'none';
            r.deleteX.style.pointerEvents = 'none';
            r.deleteX.style.opacity = '0';
          } else {
            r.deleteX.style.display = '';
          }
        }
        if (selectedId && onlineCharacterIds.has(selectedId)) {
          const firstOffline = realCharacters.find((c) => !onlineCharacterIds.has(c.id));
          if (firstOffline) {
            selectChar(firstOffline);
            return;
          }
        }
        paintSelection();
        updateActionButtons();
      };

      if (realCharacters.length > 0) {
        selectChar(realCharacters[0]);
      } else {
        // Sin personajes creados: preview VACÍO (no un body por defecto sin cabeza).
        charPreview.setEmpty();
        centerName.textContent = '';
      }
      updateActionButtons();
      charPreview.start();
      window.addEventListener('resize', onResize);

      const statusClient = new ColyseusClient(this.host.serverUrl);
      this.host.statusClient = statusClient;

      statusClient
        .joinStatusRoom()
        .then(() => {
          let isFirstMessage = true;
          statusClient.onStatusMessage((message) => {
            if (message.type === ServerMessageType.CHARACTER_STATUS_UPDATE) {
              const statusMessage = message as CharacterStatusUpdateMessage;

              if (isFirstMessage) {
                onlineCharacterIds.clear();
                isFirstMessage = false;
                for (const characterId of statusMessage.characterIds) {
                  onlineCharacterIds.add(characterId);
                }
              } else {
                for (const characterId of statusMessage.characterIds) {
                  if (statusMessage.connected) {
                    onlineCharacterIds.add(characterId);
                  } else {
                    onlineCharacterIds.delete(characterId);
                  }
                }
              }

              updateCharacterStatusUI();
            }
          });

          statusClient.onStatusRoomDisconnect(() => {
            if (!intentionalDisconnect) {
              if (this.host.currentSessionToken) {
                void this.host.api.logout(this.host.currentSessionToken).catch(() => {
                  // Ignorar errores de logout
                });
              }
              window.location.reload();
            }
          });
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('[AuthUI] Failed to connect to status room:', error);
        });

      // No revelar hasta que el muñeco esté completamente compuesto (todas sus capas) y el
      // arte de la UI (panel/botones/avatares) haya cargado: recién ahí `build` resuelve y
      // changeBackground funde el negro, mostrando la pantalla YA lista de una.
      await Promise.all([
        charPreview.whenReady(),
        this.awaitImagesLoaded(this.host.contentLayer),
      ]);
      };

      void this.host.chrome.changeBackground(entryBg, { fullScreen: true, whileHidden: build });
    });
  }

}
